import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithOrg } from '../utils/api';
import { useToast } from '../hooks/use-toast';

import {
  ArrowLeft,
  ArrowRight,
  ArrowSquareOut,
  ArrowsClockwise,
  Brain,
  Calendar,
  CaretDown,
  ChatText,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  Copy,
  Cpu,
  CreditCard,
  DeviceMobile,
  DotsThreeVertical,
  Envelope,
  Eye,
  GearSix,
  Globe,
  HardDrives,
  Lightning,
  Lock,
  Monitor,
  PaperPlaneRight,
  Pause,
  Play,
  Plus,
  Robot,
  Shield,
  Sparkle,
  Square,
  Terminal,
  Trash,
  WarningCircle,
  WifiHigh,
  WifiSlash,
  X
} from '@phosphor-icons/react';
/**
 * Operators Management Page
 * Internal admin page for managing Operators (beta feature)
 * 
 * Operators are AI agents that can execute tasks on local machines
 * through secure connectors.
 * 
 * Access controlled by user-specific beta allowlist
 */
const Operators = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeOrganization } = useOrganization();
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Access check state
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessReason, setAccessReason] = useState('');
  
  // Data state
  const [operators, setOperators] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [entitlements, setEntitlements] = useState(null);
  const [hostingActive, setHostingActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('operators');
  const [createOperatorOpen, setCreateOperatorOpen] = useState(false);
  const [createConnectorOpen, setCreateConnectorOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorDetailOpen, setOperatorDetailOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [connectorDetailOpen, setConnectorDetailOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // Track which operator is being actioned

  // Check user access on mount
  useEffect(() => {
    checkUserAccess();
  }, []);

  // Load data when access is confirmed
  useEffect(() => {
    if (hasAccess && activeOrganization) {
      loadData();
    }
  }, [hasAccess, activeOrganization]);

  // Check for create parameter and open wizard automatically
  useEffect(() => {
    if (hasAccess && searchParams.get('create') === 'true') {
      setCreateOperatorOpen(true);
      // Clear the param so refreshing doesn't reopen
      setSearchParams({}, { replace: true });
    }
  }, [hasAccess, searchParams, setSearchParams]);

  // Check for edit parameter and open edit wizard for that operator
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (hasAccess && editId && operators.length > 0) {
      const operatorToEdit = operators.find(op => op.id === editId);
      if (operatorToEdit) {
        setSelectedOperator(operatorToEdit);
        setOperatorDetailOpen(true);
        // Clear the param so refreshing doesn't reopen
        setSearchParams({}, { replace: true });
      }
    }
  }, [hasAccess, searchParams, operators, setSearchParams]);

  const checkUserAccess = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/access`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Feature is now public - always grant access if API responds
        // Tier limits are enforced on the backend when creating operators
        setHasAccess(true);
        setAccessReason(data.reason || 'public_rollout');
        setEntitlements(data.entitlements || null);
        setHostingActive(data.hosting_active || false);
      } else {
        // If API fails, still allow access - backend will enforce limits
        setHasAccess(true);
        setAccessReason('public_access');
      }
    } catch (err) {
      console.error('Error checking access:', err);
      // On network error, still allow access - let backend handle limits
      setHasAccess(true);
      setAccessReason('public_access');
    } finally {
      setAccessChecked(true);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch operators and connectors in parallel
      const [operatorsRes, connectorsRes] = await Promise.all([
        fetchWithOrg(
          `${BACKEND_URL}/api/exec/agents`,
          {},
          activeOrganization.organization_id
        ),
        fetchWithOrg(
          `${BACKEND_URL}/api/exec/connector`,
          {},
          activeOrganization.organization_id
        )
      ]);
      
      if (operatorsRes.ok) {
        const data = await operatorsRes.json();
        setOperators(data.agents || []);
        setHostingActive(data.hosting_active || false);
      }
      
      if (connectorsRes.ok) {
        const data = await connectorsRes.json();
        setConnectors(data.connectors || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load operators data');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization]);

  // Handle activation checkout
  const handleEnableHosting = async () => {
    try {
      setCheckoutLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/hosting/subscribe`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            success_url: `${window.location.origin}/agents?activation=success`,
            cancel_url: `${window.location.origin}/agents?activation=cancelled`
          })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          toast({ 
            title: 'Checkout Created', 
            description: 'Redirecting to payment...',
            variant: 'default'
          });
        }
      } else {
        const errorData = await response.json();
        toast({ 
          title: 'Checkout Failed', 
          description: errorData.detail || 'Unable to create checkout session',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast({ 
        title: 'Error', 
        description: 'Failed to initiate checkout',
        variant: 'destructive'
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Trash operator handler
  const handleDeleteOperator = async (operator) => {
    if (!window.confirm(`Are you sure you want to delete "${operator.name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operator.id}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ 
          title: 'Deleted', 
          description: `Operator "${operator.name}" has been deleted.`
        });
        loadData();
      } else {
        const errorData = await response.json();
        toast({ 
          title: 'Delete Failed', 
          description: errorData.detail?.message || errorData.detail || 'Unable to delete operator',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast({ 
        title: 'Error', 
        description: 'Failed to delete operator',
        variant: 'destructive'
      });
    }
  };

  // Trash connector handler
  const handleDeleteConnector = async (connector) => {
    if (!window.confirm(`Are you sure you want to revoke access for "${connector.name}"? This will disconnect the device.`)) {
      return;
    }
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/${connector.id}/revoke`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ 
          title: 'Access Revoked', 
          description: `Connector "${connector.name}" has been removed.`
        });
        loadData();
      } else {
        const errorData = await response.json();
        toast({ 
          title: 'Revoke Failed', 
          description: errorData.detail?.message || errorData.detail || 'Unable to revoke access',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Delete connector error:', err);
      toast({ 
        title: 'Error', 
        description: 'Failed to revoke connector access',
        variant: 'destructive'
      });
    }
  };

  // Operator action handlers
  const handleStartOperator = async (operator) => {
    setActionLoading(operator.id);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operator.id}/start`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ title: 'Started', description: `Operator "${operator.name}" is now running.` });
        loadData();
      } else {
        const errorData = await response.json();
        toast({ 
          title: 'Start Failed', 
          description: errorData.detail?.message || errorData.detail || 'Unable to start operator',
          variant: 'destructive'
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to start operator', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseOperator = async (operator) => {
    setActionLoading(operator.id);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operator.id}/pause`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ title: 'Paused', description: `Operator "${operator.name}" has been paused.` });
        loadData();
      } else {
        const errorData = await response.json();
        toast({ 
          title: 'Pause Failed', 
          description: errorData.detail?.message || errorData.detail || 'Unable to pause operator',
          variant: 'destructive'
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to pause operator', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopOperator = async (operator) => {
    setActionLoading(operator.id);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operator.id}/stop`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ title: 'Stopped', description: `Operator "${operator.name}" has been stopped.` });
        loadData();
      } else {
        const errorData = await response.json();
        toast({ 
          title: 'Stop Failed', 
          description: errorData.detail?.message || errorData.detail || 'Unable to stop operator',
          variant: 'destructive'
        });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to stop operator', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Access denied view
  if (accessChecked && !hasAccess) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md text-center"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/10 flex items-center justify-center">
            <WarningCircle className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold mb-3">Unable to Load Operators</h1>
          <p className="text-[#B0B8C4] mb-6">
            We&apos;re having trouble loading the Operators feature. Please try refreshing the page or check back later.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 rounded-lg bg-[#00E5C0] hover:bg-pink-600 transition-colors"
            >
              Refresh Page
            </button>
            <button
              onClick={() => navigate('/partner-pro/ai-workforce')}
              className="px-6 py-3 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
            >
              Back to AI Workforce
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Loading state
  if (!accessChecked || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <CircleNotch className="w-8 h-8 animate-spin text-pink-500" />
          <p className="text-[#B0B8C4]">Loading Operators...</p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const configs = {
      draft: { icon: Clock, color: 'text-[#B0B8C4] bg-[#1E2836]', label: 'Draft' },
      configured: { icon: GearSix, color: 'text-[#3B82F6] bg-blue-500/10', label: 'Configured' },
      active: { icon: Play, color: 'text-[#00E5C0] bg-[#00E5C0]/10', label: 'Active' },
      paused: { icon: Pause, color: 'text-[#F59E0B] bg-[#F59E0B]/10', label: 'Paused' },
      stopped: { icon: WarningCircle, color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Stopped' },
      error: { icon: WarningCircle, color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Error' },
    };
    const config = configs[status] || configs.draft;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const getConnectorStatusBadge = (status, isOnline) => {
    if (status === 'connected' && isOnline) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-[#00E5C0] bg-[#00E5C0]/10">
          <WifiHigh className="w-3.5 h-3.5" />
          Online
        </span>
      );
    }
    
    const configs = {
      pending: { icon: Clock, color: 'text-[#B0B8C4] bg-[#1E2836]', label: 'Awaiting Connection' },
      connected: { icon: WifiSlash, color: 'text-[#F59E0B] bg-[#F59E0B]/10', label: 'Offline' },
      disconnected: { icon: WifiSlash, color: 'text-[#F59E0B] bg-[#F59E0B]/10', label: 'Disconnected' },
      revoked: { icon: Lock, color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Revoked' },
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="border-b border-[rgba(255,255,255,0.08)]/50 bg-[#1F2937]/30 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg overflow-hidden">
                <img src="/orion-icon-192.png" alt="Orion" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold">Operators</h1>
                </div>
                <p className="text-sm text-[#B0B8C4]">
                  AI agents that operate your local machine
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Activation Status */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                hostingActive 
                  ? 'bg-[#00E5C0]/10 border border-green-500/20' 
                  : 'bg-[#1E2836] border border-[rgba(255,255,255,0.08)]'
              }`}>
                <HardDrives className={`w-4 h-4 ${hostingActive ? 'text-[#00E5C0]' : 'text-[#7D8896]'}`} />
                <span className={`text-sm ${hostingActive ? 'text-[#00E5C0]' : 'text-[#7D8896]'}`}>
                  {hostingActive ? 'Active' : 'Not Active'}
                </span>
              </div>
              
              {!hostingActive && (
                <button
                  onClick={handleEnableHosting}
                  disabled={checkoutLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
                >
                  {checkoutLoading ? (
                    <CircleNotch className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  Activate (Free)
                </button>
              )}
              
              <button
                onClick={loadData}
                className="p-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
                title="Refresh"
              >
                <ArrowsClockwise className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            {[
              { id: 'operators', label: 'Operators', icon: Robot, count: operators.length },
              { id: 'connectors', label: 'Connectors', icon: WifiHigh, count: connectors.length },
              { id: 'settings', label: 'Settings', icon: GearSix, count: null },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#1E2836] text-white'
                    : 'text-[#B0B8C4] hover:text-white hover:bg-[#1E2836]/50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <span className="px-1.5 py-0.5 text-xs rounded bg-[#1A2030]">{tab.count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'operators' && (
            <motion.div
              key="operators"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Create Operator Card */}
              <div className="mb-6">
                <button
                  onClick={() => setCreateOperatorOpen(true)}
                  className="w-full p-6 rounded-xl border-2 border-dashed border-[rgba(255,255,255,0.08)] hover:border-[#00E5C0]/50 hover:bg-[#1F2937]/50 transition-all group"
                >
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#1E2836] group-hover:bg-[#00E5C0]/10 transition-colors flex items-center justify-center">
                      <Plus className="w-6 h-6 text-[#B0B8C4] group-hover:text-[#00E5C0] transition-colors" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-[#F0F4F8] group-hover:text-white transition-colors">Create Operator</p>
                      <p className="text-sm text-[#7D8896]">Set up a new AI operator with local machine access</p>
                    </div>
                  </div>
                </button>
              </div>
              
              {/* Operators List */}
              {operators.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1E2836]/50 flex items-center justify-center">
                    <Robot className="w-8 h-8 text-[#5C6778]" />
                  </div>
                  <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">No Operators Yet</h3>
                  <p className="text-[#7D8896] max-w-md mx-auto">
                    Create your first operator to start running tasks on your local machine through a secure connector.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {operators.map(operator => (
                    <OperatorCard 
                      key={operator.id} 
                      operator={operator} 
                      hostingActive={hostingActive}
                      onSelect={setSelectedOperator}
                      onOpenDetail={(op) => {
                        setSelectedOperator(op);
                        setOperatorDetailOpen(true);
                      }}
                      onDelete={handleDeleteOperator}
                      onStart={handleStartOperator}
                      onPause={handlePauseOperator}
                      onStop={handleStopOperator}
                      actionLoading={actionLoading}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
          
          {activeTab === 'settings' && (
            <SettingsTab 
              entitlements={entitlements} 
              hostingActive={hostingActive}
              onEnableHosting={handleEnableHosting}
              checkoutLoading={checkoutLoading}
              orgId={activeOrganization?.organization_id}
              operators={operators}
            />
          )}
          
          {activeTab === 'connectors' && (
            <motion.div
              key="connectors"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Info Banner */}
              <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-start gap-3">
                  <WifiHigh className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-300">What are Connectors?</h3>
                    <p className="text-sm text-blue-200/70 mt-1">
                      Connectors are the bridge between your Operators and your local machine. Each device needs one connector installed.
                      If a connector becomes orphaned (not linked to an Operator), you can revoke it here to clean up.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Connectors List */}
              {connectors.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#1E2836]/50 flex items-center justify-center">
                    <WifiHigh className="w-8 h-8 text-[#5C6778]" />
                  </div>
                  <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">No Connectors</h3>
                  <p className="text-[#7D8896] max-w-md mx-auto">
                    Connectors are created when you set up an Operator with a device. You&apos;ll see them here once connected.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {connectors.map(connector => (
                    <div 
                      key={connector.id}
                      className="p-4 rounded-xl bg-[#1F2937]/50 border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.08)] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            connector.is_online 
                              ? 'bg-[#00E5C0]/20' 
                              : connector.status === 'revoked' 
                                ? 'bg-[#FF6B6B]/20' 
                                : 'bg-[#1E2836]'
                          }`}>
                            {connector.is_online ? (
                              <WifiHigh className="w-5 h-5 text-[#00E5C0]" />
                            ) : connector.status === 'revoked' ? (
                              <WifiSlash className="w-5 h-5 text-[#FF6B6B]" />
                            ) : (
                              <WifiSlash className="w-5 h-5 text-[#7D8896]" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-white">{connector.name || 'Unnamed Connector'}</h3>
                              {getStatusBadge(connector.status, connector.is_online)}
                            </div>
                            <p className="text-sm text-[#B0B8C4] mt-0.5">
                              {connector.device_name || 'Unknown Device'}
                              {connector.device_os && ` • ${connector.device_os}`}
                            </p>
                            {connector.last_heartbeat_at && (
                              <p className="text-xs text-[#7D8896] mt-1">
                                Last seen: {new Date(connector.last_heartbeat_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {connector.status !== 'revoked' && (
                            <button
                              onClick={() => handleDeleteConnector(connector)}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FF6B6B]/10 text-[#FF6B6B] hover:bg-[#FF6B6B]/20 transition-colors text-sm"
                            >
                              <Trash className="w-4 h-4" />
                              Revoke Access
                            </button>
                          )}
                          {connector.status === 'revoked' && (
                            <span className="text-xs text-[#7D8896] px-3 py-1.5">
                              Access Revoked
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Additional Info */}
                      <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.08)]">
                        <div className="flex flex-wrap gap-4 text-xs text-[#7D8896]">
                          <span>ID: {connector.id?.slice(0, 8)}...</span>
                          {connector.created_at && (
                            <span>Created: {new Date(connector.created_at).toLocaleDateString()}</span>
                          )}
                          {connector.ip_address && (
                            <span>IP: {connector.ip_address}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Create Operator Modal */}
      <CreateOperatorModal
        isOpen={createOperatorOpen}
        onClose={() => setCreateOperatorOpen(false)}
        onSuccess={() => {
          setCreateOperatorOpen(false);
          loadData();
        }}
        orgId={activeOrganization?.organization_id}
        navigateToOperator={(operatorId) => navigate(`/partner-pro/ai-workforce/operators/${operatorId}`)}
      />
      
      {/* Create Connector Modal */}
      <CreateConnectorModal
        isOpen={createConnectorOpen}
        onClose={() => setCreateConnectorOpen(false)}
        onSuccess={() => {
          setCreateConnectorOpen(false);
          loadData();
        }}
        orgId={activeOrganization?.organization_id}
      />
      
      {/* Edit Operator Modal - reuses CreateOperatorModal in edit mode */}
      <CreateOperatorModal
        isOpen={operatorDetailOpen}
        onClose={() => {
          setOperatorDetailOpen(false);
          setSelectedOperator(null);
        }}
        onSuccess={() => {
          setOperatorDetailOpen(false);
          setSelectedOperator(null);
          loadData();
        }}
        orgId={activeOrganization?.organization_id}
        operator={selectedOperator}
      />
      
      {/* Connector Detail Modal */}
      <ConnectorDetailModal
        isOpen={connectorDetailOpen}
        onClose={() => {
          setConnectorDetailOpen(false);
          setSelectedConnector(null);
        }}
        connector={selectedConnector}
        onDelete={() => {
          if (selectedConnector) {
            handleDeleteConnector(selectedConnector);
          }
          setConnectorDetailOpen(false);
          setSelectedConnector(null);
        }}
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// OPERATOR CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const OperatorCard = ({ operator, hostingActive, onSelect, onOpenDetail, onDelete, onStart, onPause, onStop, actionLoading }) => {
  const getStatusBadge = (status) => {
    const configs = {
      draft: { icon: Clock, color: 'text-[#B0B8C4] bg-[#1E2836]', label: 'Draft' },
      configured: { icon: GearSix, color: 'text-[#3B82F6] bg-blue-500/10', label: 'Configured' },
      active: { icon: Play, color: 'text-[#00E5C0] bg-[#00E5C0]/10', label: 'Active' },
      paused: { icon: Pause, color: 'text-[#F59E0B] bg-[#F59E0B]/10', label: 'Paused' },
      stopped: { icon: WarningCircle, color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Stopped' },
      error: { icon: WarningCircle, color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Error' },
    };
    const config = configs[status] || configs.draft;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const isLoading = actionLoading === operator.id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-xl bg-[#1F2937]/50 border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.08)] transition-all group"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src="/orion-icon-192.png" alt="Orion" className="w-full h-full object-cover" />
          </div>
          <div>
            <h3 className="font-medium text-white mb-1">{operator.name}</h3>
            <p className="text-sm text-[#B0B8C4] mb-3">{operator.description || 'No description'}</p>
            <div className="flex items-center gap-3 flex-wrap">
              {getStatusBadge(operator.status)}
              {operator.memory_enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-[#00E5C0]/10 text-[#00E5C0]">
                  <Brain className="w-3 h-3" />
                  Memory
                </span>
              )}
              {!operator.can_run && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-[#F59E0B]/10 text-[#F59E0B]">
                  <WarningCircle className="w-3 h-3" />
                  Activation Required
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onOpenDetail(operator)}
            className="p-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
            title="Edit Operator"
          >
            <GearSix className="w-4 h-4 text-[#B0B8C4]" />
          </button>
          <button
            onClick={() => onDelete(operator)}
            className="p-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 transition-colors"
            title="Delete Operator"
          >
            <Trash className="w-4 h-4 text-[#FF6B6B]" />
          </button>
          
          {/* Start button - for draft, configured, stopped, or paused status */}
          {operator.can_run && ['draft', 'configured', 'stopped', 'paused'].includes(operator.status) && (
            <button
              onClick={() => onStart(operator)}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#00E5C0]/10 hover:bg-[#00E5C0]/20 text-[#00E5C0] transition-colors disabled:opacity-50"
              title="Start Operator"
            >
              {isLoading ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Start
            </button>
          )}
          
          {/* Pause button - for active status */}
          {operator.status === 'active' && (
            <button
              onClick={() => onPause(operator)}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 text-[#F59E0B] transition-colors disabled:opacity-50"
              title="Pause Operator"
            >
              {isLoading ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
              Pause
            </button>
          )}
          
          {/* Stop button - for active or paused status */}
          {['active', 'paused'].includes(operator.status) && (
            <button
              onClick={() => onStop(operator)}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] transition-colors disabled:opacity-50"
              title="Stop Operator"
            >
              {isLoading ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
              Stop
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONNECTOR CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ConnectorCard = ({ connector, getStatusBadge, onEdit, onDelete }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-xl bg-[#1F2937]/50 border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.08)] transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
            <DeviceMobile className="w-6 h-6 text-[#3B82F6]" />
          </div>
          <div>
            <h3 className="font-medium text-white mb-1">{connector.name}</h3>
            <p className="text-sm text-[#B0B8C4] mb-3">
              {connector.device_name || 'Unknown Device'} 
              {connector.device_os && ` • ${connector.device_os}`}
            </p>
            <div className="flex items-center gap-3">
              {getStatusBadge(connector.status, connector.is_online)}
              {connector.last_heartbeat_at && (
                <span className="text-xs text-[#7D8896]">
                  Last seen: {new Date(connector.last_heartbeat_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(connector)}
            className="p-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
            title="View Details"
          >
            <GearSix className="w-4 h-4 text-[#B0B8C4]" />
          </button>
          {connector.status !== 'revoked' && (
            <button
              onClick={() => onDelete(connector)}
              className="p-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 transition-colors"
              title="Revoke Access"
            >
              <Trash className="w-4 h-4 text-[#FF6B6B]" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONNECTOR DETAIL MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const ConnectorDetailModal = ({ isOpen, onClose, connector, onDelete }) => {
  if (!isOpen || !connector) return null;

  const getStatusConfig = (status, isOnline) => {
    if (status === 'revoked') {
      return { color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Revoked', icon: WarningCircle };
    }
    if (isOnline) {
      return { color: 'text-[#00E5C0] bg-[#00E5C0]/10', label: 'Online', icon: WifiHigh };
    }
    return { color: 'text-[#B0B8C4] bg-[#1E2836]', label: 'Offline', icon: WifiSlash };
  };

  const statusConfig = getStatusConfig(connector.status, connector.is_online);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-[rgba(255,255,255,0.08)]">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
                <DeviceMobile className="w-7 h-7 text-[#3B82F6]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">{connector.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig.label}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors"
            >
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">Device Name</label>
              <p className="text-[#F0F4F8]">{connector.device_name || 'Unknown'}</p>
            </div>
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">Operating System</label>
              <p className="text-[#F0F4F8]">{connector.device_os || 'Unknown'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">Registered</label>
              <p className="text-[#F0F4F8] text-sm">
                {connector.created_at ? new Date(connector.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">Last Active</label>
              <p className="text-[#F0F4F8] text-sm">
                {connector.last_heartbeat_at ? new Date(connector.last_heartbeat_at).toLocaleString() : 'Never'}
              </p>
            </div>
          </div>

          {connector.ip_address && (
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">IP Address</label>
              <p className="text-[#F0F4F8] font-mono text-sm">{connector.ip_address}</p>
            </div>
          )}

          {connector.capabilities && connector.capabilities.length > 0 && (
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-2">Capabilities</label>
              <div className="flex flex-wrap gap-2">
                {connector.capabilities.map((cap, i) => (
                  <span key={i} className="px-2 py-1 rounded-lg bg-[#1E2836] text-[#F0F4F8] text-xs">
                    {cap}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-[rgba(255,255,255,0.08)] bg-[#1F2937]/50">
          <div className="flex items-center justify-between">
            {connector.status !== 'revoked' && (
              <button
                onClick={onDelete}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] transition-colors"
              >
                <Trash className="w-4 h-4" />
                Revoke Access
              </button>
            )}
            <button
              onClick={onClose}
              className="ml-auto px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] text-[#F0F4F8] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SETTINGS TAB COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const SettingsTab = ({ entitlements, hostingActive, onEnableHosting, checkoutLoading, orgId, operators }) => {
  const { toast } = useToast();
  
  // Telegram state
  const [telegramSetupOpen, setTelegramSetupOpen] = useState(false);
  const [telegramConfigOpen, setTelegramConfigOpen] = useState(false);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [selectedOperatorForTelegram, setSelectedOperatorForTelegram] = useState(null);
  
  // WhatsApp state
  const [whatsappSetupOpen, setWhatsappSetupOpen] = useState(false);
  const [whatsappConfigOpen, setWhatsappConfigOpen] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [selectedOperatorForWhatsapp, setSelectedOperatorForWhatsapp] = useState(null);
  
  // Check chat integration statuses on mount
  useEffect(() => {
    loadChatSettings();
  }, [orgId]);
  
  const loadChatSettings = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/settings`,
        {},
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        setTelegramStatus(data.telegram);
        setWhatsappStatus(data.whatsapp);
      }
    } catch (err) {
      console.error('Error loading chat settings:', err);
    }
  };
  
  const checkTelegramStatus = async () => {
    await loadChatSettings();
  };
  
  const checkWhatsappStatus = async () => {
    await loadChatSettings();
  };
  
  const handleTelegramConnect = async () => {
    if (!selectedOperatorForTelegram) {
      toast({ 
        title: 'Select Operator', 
        description: 'Please select an operator to connect with Telegram',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setTelegramLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/telegram/generate-link`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agent_id: selectedOperatorForTelegram
          })
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.pairing_link) {
          // Open Telegram link
          window.open(data.pairing_link, '_blank');
          toast({ 
            title: 'Telegram Link Generated', 
            description: 'Click the link to connect your Telegram to this operator'
          });
        } else if (data.bot_username) {
          // Show manual pairing instructions
          toast({ 
            title: 'Connect to Bot', 
            description: `Search for @${data.bot_username} in Telegram and send /start ${data.pairing_code}`
          });
        }
        
        setTelegramSetupOpen(false);
      } else {
        const error = await response.json();
        const errorMsg = typeof error.detail === 'object' 
          ? error.detail.message 
          : error.detail || 'Failed to generate Telegram link';
        toast({ 
          title: 'Connection Failed', 
          description: errorMsg,
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Telegram connect error:', err);
      toast({ 
        title: 'Error', 
        description: 'Failed to connect Telegram',
        variant: 'destructive'
      });
    } finally {
      setTelegramLoading(false);
    }
  };
  
  const handleWhatsappConnect = async () => {
    if (!selectedOperatorForWhatsapp) {
      toast({ 
        title: 'Select Operator', 
        description: 'Please select an operator to connect with WhatsApp',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setWhatsappLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/whatsapp/generate-link`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agent_id: selectedOperatorForWhatsapp
          })
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.qr_code_url) {
          // Show QR code for scanning
          toast({ 
            title: 'Scan QR Code', 
            description: 'Scan the QR code with WhatsApp to connect'
          });
        } else if (data.phone_number) {
          // Show manual connection instructions
          toast({ 
            title: 'Connect via WhatsApp', 
            description: `Send "PAIR:${data.pairing_code}" to ${data.phone_number}`
          });
        }
        
        setWhatsappSetupOpen(false);
        checkWhatsappStatus(); // ArrowsClockwise status
      } else {
        const error = await response.json();
        const errorMsg = typeof error.detail === 'object' 
          ? error.detail.message 
          : error.detail || 'Failed to generate WhatsApp link';
        toast({ 
          title: 'Connection Failed', 
          description: errorMsg,
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('WhatsApp connect error:', err);
      toast({ 
        title: 'Error', 
        description: 'Failed to connect WhatsApp',
        variant: 'destructive'
      });
    } finally {
      setWhatsappLoading(false);
    }
  };
  
  return (
    <motion.div
      key="settings"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div className="max-w-2xl space-y-6">
        {/* Entitlements Card */}
        <div className="p-6 rounded-xl bg-[#1F2937]/50 border border-[rgba(255,255,255,0.08)]">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Lightning className="w-5 h-5 text-[#F59E0B]" />
            Your Entitlements
          </h3>
          
          {entitlements && entitlements.max_execution_agents !== undefined ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-[#1E2836]/50">
                <p className="text-sm text-[#B0B8C4] mb-1">Operator Allowance</p>
                <p className="text-2xl font-semibold">
                  {entitlements.max_execution_agents === -1 ? '∞' : entitlements.max_execution_agents}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-[#1E2836]/50">
                <p className="text-sm text-[#B0B8C4] mb-1">Connector Allowance</p>
                <p className="text-2xl font-semibold">
                  {entitlements.max_connectors === -1 ? '∞' : entitlements.max_connectors}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-[#1E2836]/50">
                <p className="text-sm text-[#B0B8C4] mb-1">Telegram</p>
                <p className="text-lg font-medium">
                  {entitlements.telegram_enabled ? (
                    <span className="text-[#00E5C0]">Enabled</span>
                  ) : (
                    <span className="text-[#7D8896]">Disabled</span>
                  )}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-[#1E2836]/50">
                <p className="text-sm text-[#B0B8C4] mb-1">WhatsApp</p>
                <p className="text-lg font-medium">
                  {entitlements.whatsapp_enabled ? (
                    <span className="text-[#00E5C0]">Enabled</span>
                  ) : (
                    <span className="text-[#7D8896]">Disabled</span>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-[#B0B8C4]">Enable the feature to view entitlements</p>
          )}
        </div>
        
        {/* Chat Integrations */}
        <div className="p-6 rounded-xl bg-[#1F2937]/50 border border-[rgba(255,255,255,0.08)]">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <ChatText className="w-5 h-5 text-[#3B82F6]" />
            Chat Integrations
          </h3>
          <p className="text-sm text-[#B0B8C4] mb-4">
            Connect Telegram or WhatsApp to interact with your operators via chat.
            Receive notifications and approve/deny actions directly from your phone.
          </p>
          
          <div className="space-y-3">
            {/* Telegram Integration */}
            <div className="p-4 rounded-lg bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <PaperPlaneRight className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Telegram</p>
                    <p className="text-sm text-[#7D8896]">
                      {telegramStatus?.configured 
                        ? telegramStatus?.bot_username 
                          ? `@${telegramStatus.bot_username}` 
                          : 'Bot configured'
                        : 'Not configured'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {telegramStatus?.configured && (
                    <button
                      onClick={() => setTelegramSetupOpen(true)}
                      className="px-3 py-1.5 text-sm rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-[#3B82F6] transition-colors"
                    >
                      Link Operator
                    </button>
                  )}
                  <button
                    onClick={() => setTelegramConfigOpen(true)}
                    className="px-3 py-1.5 text-sm rounded-lg bg-[#1A2030] hover:bg-zinc-600 text-[#F0F4F8] transition-colors"
                  >
                    {telegramStatus?.configured ? 'Settings' : 'Configure'}
                  </button>
                </div>
              </div>
              
              {!telegramStatus?.configured && (
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-sm">
                  <p className="text-blue-300 font-medium mb-1">Setup Instructions</p>
                  <ol className="text-[#B0B8C4] space-y-1">
                    <li>1. Open Telegram and search for <strong className="text-blue-300">@BotFather</strong></li>
                    <li>2. PaperPlaneRight <code className="bg-[#1A2030] px-1 rounded">/newbot</code> and follow the prompts</li>
                    <li>3. Copy the bot token and click <strong className="text-blue-300">Configure</strong> above</li>
                  </ol>
                </div>
              )}
              
              {telegramStatus?.connected && telegramStatus.bindings && (
                <div className="mt-3 space-y-2">
                  {telegramStatus.bindings.map((binding, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded bg-[#1A2030]/30">
                      <div className="flex items-center gap-2">
                        <Robot className="w-4 h-4 text-[#B0B8C4]" />
                        <span className="text-sm">{binding.agent_name}</span>
                      </div>
                      <span className="text-xs text-[#7D8896]">@{binding.channel_username || 'private'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* WhatsApp Integration */}
            <div className="p-4 rounded-lg bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#00E5C0]/10 flex items-center justify-center">
                    <ChatText className="w-5 h-5 text-[#00E5C0]" />
                  </div>
                  <div>
                    <p className="font-medium text-white">WhatsApp</p>
                    <p className="text-sm text-[#7D8896]">
                      {whatsappStatus?.configured 
                        ? whatsappStatus?.phone_number_id 
                          ? `Phone: ${whatsappStatus.phone_number_id}` 
                          : 'Configured'
                        : 'Not configured'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {whatsappStatus?.configured && (
                    <button
                      onClick={() => setWhatsappSetupOpen(true)}
                      className="px-3 py-1.5 text-sm rounded-lg bg-[#00E5C0]/10 hover:bg-[#00E5C0]/20 text-[#00E5C0] transition-colors"
                    >
                      Link Operator
                    </button>
                  )}
                  <button
                    onClick={() => setWhatsappConfigOpen(true)}
                    className="px-3 py-1.5 text-sm rounded-lg bg-[#1A2030] hover:bg-zinc-600 text-[#F0F4F8] transition-colors"
                  >
                    {whatsappStatus?.configured ? 'Settings' : 'Configure'}
                  </button>
                </div>
              </div>
              
              {!whatsappStatus?.configured && (
                <div className="p-3 rounded-lg bg-[#00E5C0]/5 border border-green-500/20 text-sm">
                  <p className="text-[#00E5C0] font-medium mb-1">Setup Instructions</p>
                  <ol className="text-[#B0B8C4] space-y-1">
                    <li>1. Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] underline">Meta for Developers</a></li>
                    <li>2. Create a WhatsApp Business App</li>
                    <li>3. Get your credentials from WhatsApp &gt; API Setup</li>
                    <li>4. Click <strong className="text-[#00E5C0]">Configure</strong> above</li>
                  </ol>
                </div>
              )}
              
              {whatsappStatus?.connected && whatsappStatus.bindings && (
                <div className="mt-3 space-y-2">
                  {whatsappStatus.bindings.map((binding, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded bg-[#1A2030]/30">
                      <div className="flex items-center gap-2">
                        <Robot className="w-4 h-4 text-[#B0B8C4]" />
                        <span className="text-sm">{binding.agent_name}</span>
                      </div>
                      <span className="text-xs text-[#7D8896]">{binding.phone_number || 'Connected'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Operator Activation */}
        <div className="p-6 rounded-xl bg-[#1F2937]/50 border border-[rgba(255,255,255,0.08)]">
          <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#00E5C0]" />
            Operator Activation
          </h3>
          
          {hostingActive ? (
            <div className="p-4 rounded-lg bg-[#00E5C0]/5 border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-[#00E5C0]" />
                <span className="font-medium text-[#00E5C0]">Active</span>
              </div>
              <p className="text-sm text-[#B0B8C4]">Free hosting • Compute via credits</p>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-[#F59E0B]/5 border border-yellow-500/20">
              <div className="flex items-center gap-2 mb-2">
                <WarningCircle className="w-5 h-5 text-[#F59E0B]" />
                <span className="font-medium text-[#F59E0B]">Activation Required</span>
              </div>
              <p className="text-sm text-[#B0B8C4] mb-4">
                Activate this Operator to run tasks on your devices. Free hosting — compute uses credits.
              </p>
              <button 
                onClick={onEnableHosting}
                disabled={checkoutLoading}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
              >
                {checkoutLoading ? (
                  <span className="flex items-center gap-2">
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  'Activate Operator'
                )}
              </button>
            </div>
          )}
        </div>
        
        {/* Device Requirements Notice */}
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <div className="flex gap-3">
            <WarningCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-300 mb-1">Device Requirements</h4>
              <p className="text-sm text-[#B0B8C4]">
                For operators to execute commands, your connected device must:
              </p>
              <ul className="text-sm text-[#B0B8C4] mt-2 space-y-1">
                <li>• Stay <strong className="text-amber-300">awake</strong> (disable sleep mode)</li>
                <li>• Stay <strong className="text-amber-300">online</strong> (maintain internet connection)</li>
                <li>• Have the connector <strong className="text-amber-300">running</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Telegram Setup Modal */}
      <TelegramSetupModal
        isOpen={telegramSetupOpen}
        onClose={() => setTelegramSetupOpen(false)}
        operators={operators}
        selectedOperator={selectedOperatorForTelegram}
        onSelectOperator={setSelectedOperatorForTelegram}
        onConnect={handleTelegramConnect}
        loading={telegramLoading}
      />
      
      {/* Telegram Config Modal */}
      <TelegramConfigModal
        isOpen={telegramConfigOpen}
        onClose={() => setTelegramConfigOpen(false)}
        orgId={orgId}
        currentStatus={telegramStatus}
        onSuccess={() => {
          setTelegramConfigOpen(false);
          loadChatSettings();
        }}
      />
      
      {/* WhatsApp Setup Modal */}
      <WhatsAppSetupModal
        isOpen={whatsappSetupOpen}
        onClose={() => setWhatsappSetupOpen(false)}
        operators={operators}
        selectedOperator={selectedOperatorForWhatsapp}
        onSelectOperator={setSelectedOperatorForWhatsapp}
        onConnect={handleWhatsappConnect}
        loading={whatsappLoading}
      />
      
      {/* WhatsApp Config Modal */}
      <WhatsAppConfigModal
        isOpen={whatsappConfigOpen}
        onClose={() => setWhatsappConfigOpen(false)}
        orgId={orgId}
        currentStatus={whatsappStatus}
        onSuccess={() => {
          setWhatsappConfigOpen(false);
          loadChatSettings();
        }}
      />
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TELEGRAM SETUP MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const TelegramSetupModal = ({ isOpen, onClose, operators, selectedOperator, onSelectOperator, onConnect, loading }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="border-b border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <PaperPlaneRight className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Connect Telegram</h2>
                <p className="text-sm text-[#B0B8C4]">Link an operator to your Telegram</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors">
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Select Operator
              </label>
              
              {operators && operators.length > 0 ? (
                <div className="space-y-2">
                  {operators.map(op => (
                    <button
                      key={op.id}
                      onClick={() => onSelectOperator(op.id)}
                      className={`w-full p-3 rounded-lg border text-left transition-all ${
                        selectedOperator === op.id
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg overflow-hidden">
                          <img src="/orion-icon-192.png" alt="Orion" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{op.name}</p>
                          <p className="text-xs text-[#7D8896]">{op.description || 'No description'}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-[#1E2836]/50 text-center">
                  <p className="text-[#B0B8C4] text-sm">No operators available</p>
                  <p className="text-[#7D8896] text-xs mt-1">Create an operator first</p>
                </div>
              )}
            </div>
            
            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <h4 className="font-medium text-blue-300 text-sm mb-2">How it works</h4>
              <ol className="text-sm text-[#B0B8C4] space-y-1">
                <li>1. Select an operator above</li>
                <li>2. Click Connect to generate a pairing link</li>
                <li>3. Open the link in Telegram</li>
                <li>4. Start chatting with your operator!</li>
              </ol>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="border-t border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
            >
              Cancel
            </button>
            
            <button
              onClick={onConnect}
              disabled={loading || !selectedOperator}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <>
                  <CircleNotch className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <PaperPlaneRight className="w-4 h-4" />
                  Connect
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WHATSAPP SETUP MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const WhatsAppSetupModal = ({ isOpen, onClose, operators, selectedOperator, onSelectOperator, onConnect, loading }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="border-b border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00E5C0]/10 flex items-center justify-center">
                <ChatText className="w-5 h-5 text-[#00E5C0]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Connect WhatsApp</h2>
                <p className="text-sm text-[#B0B8C4]">Link an operator to your WhatsApp</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors">
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Select Operator
              </label>
              
              {operators && operators.length > 0 ? (
                <div className="space-y-2">
                  {operators.map(op => (
                    <button
                      key={op.id}
                      onClick={() => onSelectOperator(op.id)}
                      className={`w-full p-3 rounded-lg border text-left transition-all ${
                        selectedOperator === op.id
                          ? 'border-green-500 bg-[#00E5C0]/10'
                          : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg overflow-hidden">
                          <img src="/orion-icon-192.png" alt="Orion" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{op.name}</p>
                          <p className="text-xs text-[#7D8896]">{op.description || 'No description'}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-[#1E2836]/50 text-center">
                  <p className="text-[#B0B8C4] text-sm">No operators available</p>
                  <p className="text-[#7D8896] text-xs mt-1">Create an operator first</p>
                </div>
              )}
            </div>
            
            <div className="p-4 rounded-lg bg-[#00E5C0]/5 border border-green-500/20">
              <h4 className="font-medium text-[#00E5C0] text-sm mb-2">How it works</h4>
              <ol className="text-sm text-[#B0B8C4] space-y-1">
                <li>1. Select an operator above</li>
                <li>2. Click Connect to generate a pairing code</li>
                <li>3. Send the code to our WhatsApp number</li>
                <li>4. Start chatting with your operator!</li>
              </ol>
            </div>
            
            <div className="p-3 rounded-lg bg-[#1E2836]/50 text-sm">
              <p className="text-[#B0B8C4]">
                <strong className="text-[#F0F4F8]">Note:</strong> WhatsApp integration uses the WhatsApp Business API. 
                You&apos;ll receive a pairing code to send to the configured business number.
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="border-t border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
            >
              Cancel
            </button>
            
            <button
              onClick={onConnect}
              disabled={loading || !selectedOperator}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <>
                  <CircleNotch className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ChatText className="w-4 h-4" />
                  Connect
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TELEGRAM CONFIG MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const TelegramConfigModal = ({ isOpen, onClose, orgId, currentStatus, onSuccess }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [botToken, setBotToken] = useState('');
  
  const handleSave = async () => {
    if (!botToken.trim()) {
      toast({ 
        title: 'Error', 
        description: 'Please enter a bot token',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/settings/telegram`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bot_token: botToken })
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        toast({ 
          title: 'Success', 
          description: data.message || `Connected to @${data.bot_username}`
        });
        setBotToken('');
        onSuccess();
      } else {
        const error = await response.json();
        const errorMsg = typeof error.detail === 'object' 
          ? error.detail.message || error.detail 
          : error.detail || 'Failed to configure Telegram';
        toast({ 
          title: 'Configuration Failed', 
          description: errorMsg,
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Telegram config error:', err);
      toast({ 
        title: 'Error', 
        description: 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemove = async () => {
    try {
      setLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/settings/telegram`,
        { method: 'DELETE' },
        orgId
      );
      
      if (response.ok) {
        toast({ title: 'Removed', description: 'Telegram configuration removed' });
        onSuccess();
      }
    } catch (err) {
      console.error('Remove error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-2xl"
      >
        <div className="border-b border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <PaperPlaneRight className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Configure Telegram</h2>
                <p className="text-sm text-[#B0B8C4]">Enter your bot token from @BotFather</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors">
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {currentStatus?.configured ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-[#00E5C0]/5 border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-[#00E5C0]" />
                  <span className="font-medium text-[#00E5C0]">Robot Connected</span>
                </div>
                <p className="text-sm text-[#B0B8C4]">
                  Username: <span className="text-white">@{currentStatus.bot_username}</span>
                </p>
                <p className="text-sm text-[#B0B8C4]">
                  Token: <span className="text-[#7D8896]">{currentStatus.bot_token}</span>
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-[#F59E0B]/5 border border-yellow-500/20 text-sm">
                <p className="text-[#F59E0B]">
                  To change the bot, enter a new token below or remove the current configuration.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20 mb-4">
              <h4 className="font-medium text-blue-300 text-sm mb-2">How to get a bot token</h4>
              <ol className="text-sm text-[#B0B8C4] space-y-1">
                <li>1. Open Telegram and search for <strong className="text-blue-300">@BotFather</strong></li>
                <li>2. PaperPlaneRight <code className="bg-[#1A2030] px-1 rounded">/newbot</code></li>
                <li>3. Follow the prompts to name your bot</li>
                <li>4. Copy the token that looks like: <code className="bg-[#1A2030] px-1 rounded text-xs">123456789:ABC...</code></li>
              </ol>
            </div>
          )}
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
              Robot Token
            </label>
            <input
              type="text"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456789:ABC-DEF..."
              className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-blue-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
            />
          </div>
        </div>
        
        <div className="border-t border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex justify-between">
            {currentStatus?.configured ? (
              <button
                onClick={handleRemove}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] transition-colors"
              >
                Remove
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
              >
                Cancel
              </button>
            )}
            
            <button
              onClick={handleSave}
              disabled={loading || !botToken.trim()}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <>
                  <CircleNotch className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {currentStatus?.configured ? 'Update' : 'Save'}
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WHATSAPP CONFIG MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const WhatsAppConfigModal = ({ isOpen, onClose, orgId, currentStatus, onSuccess }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone_number_id: '',
    access_token: '',
    business_account_id: ''
  });
  
  const handleSave = async () => {
    if (!formData.phone_number_id || !formData.access_token || !formData.business_account_id) {
      toast({ 
        title: 'Error', 
        description: 'All fields are required',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/settings/whatsapp`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        toast({ 
          title: 'Success', 
          description: data.message || 'WhatsApp configured successfully'
        });
        setFormData({ phone_number_id: '', access_token: '', business_account_id: '' });
        onSuccess();
      } else {
        const error = await response.json();
        const errorMsg = typeof error.detail === 'object' 
          ? error.detail.message || error.detail 
          : error.detail || 'Failed to configure WhatsApp';
        toast({ 
          title: 'Configuration Failed', 
          description: errorMsg,
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('WhatsApp config error:', err);
      toast({ 
        title: 'Error', 
        description: 'Failed to save configuration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemove = async () => {
    try {
      setLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/settings/whatsapp`,
        { method: 'DELETE' },
        orgId
      );
      
      if (response.ok) {
        toast({ title: 'Removed', description: 'WhatsApp configuration removed' });
        onSuccess();
      }
    } catch (err) {
      console.error('Remove error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#00E5C0]/10 flex items-center justify-center">
                <ChatText className="w-5 h-5 text-[#00E5C0]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Configure WhatsApp</h2>
                <p className="text-sm text-[#B0B8C4]">Enter your WhatsApp Business API credentials</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors">
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {currentStatus?.configured ? (
            <div className="p-4 rounded-lg bg-[#00E5C0]/5 border border-green-500/20 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-[#00E5C0]" />
                <span className="font-medium text-[#00E5C0]">WhatsApp Configured</span>
              </div>
              <p className="text-sm text-[#B0B8C4]">
                Phone Number ID: <span className="text-white">{currentStatus.phone_number_id}</span>
              </p>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-[#00E5C0]/5 border border-green-500/20 mb-4">
              <h4 className="font-medium text-[#00E5C0] text-sm mb-2">Where to find these credentials</h4>
              <ol className="text-sm text-[#B0B8C4] space-y-1">
                <li>1. Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] underline">Meta for Developers</a></li>
                <li>2. Navigate to your WhatsApp Business App</li>
                <li>3. Find credentials under WhatsApp &gt; API Setup</li>
              </ol>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Phone Number ID
              </label>
              <input
                type="text"
                value={formData.phone_number_id}
                onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                placeholder="123456789012345"
                className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-green-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Business Account ID
              </label>
              <input
                type="text"
                value={formData.business_account_id}
                onChange={(e) => setFormData({ ...formData, business_account_id: e.target.value })}
                placeholder="123456789012345"
                className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-green-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                Access Token
              </label>
              <input
                type="password"
                value={formData.access_token}
                onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                placeholder="EAAxxxxxxx..."
                className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-green-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
              />
            </div>
          </div>
        </div>
        
        <div className="border-t border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex justify-between">
            {currentStatus?.configured ? (
              <button
                onClick={handleRemove}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] transition-colors"
              >
                Remove
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
              >
                Cancel
              </button>
            )}
            
            <button
              onClick={handleSave}
              disabled={loading || !formData.phone_number_id || !formData.access_token || !formData.business_account_id}
              className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <>
                  <CircleNotch className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {currentStatus?.configured ? 'Update' : 'Save'}
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAIRING CODE DISPLAY COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const PairingCodeDisplay = ({ code }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast({ title: 'Copied!', description: 'Pairing code copied to clipboard' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to copy', variant: 'destructive' });
    }
  };
  
  return (
    <div className="p-4 rounded-lg bg-[#00E5C0]/10 border border-green-500/20">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle className="w-5 h-5 text-[#00E5C0]" />
        <span className="font-medium text-[#00E5C0]">Pairing Code Generated!</span>
      </div>
      <div className="relative bg-[#1F2937] rounded-lg p-4 mb-3">
        <p className="text-2xl font-mono text-center tracking-widest text-white pr-10">
          {code}
        </p>
        <button
          onClick={handleCopy}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-[#1A2030] transition-colors"
          title="Copy pairing code"
        >
          {copied ? (
            <Check className="w-5 h-5 text-[#00E5C0]" />
          ) : (
            <Copy className="w-5 h-5 text-[#B0B8C4]" />
          )}
        </button>
      </div>
      <p className="text-sm text-[#B0B8C4]">
        Run the connector script on your local machine and enter this code when prompted.
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// QUICK INSTALL CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const QuickInstallCard = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('windows');
  const [copied, setCopied] = useState(null);
  
  // Use the actual API endpoint for connector scripts
  const apiBase = window.location.hostname.includes('localhost') 
    ? 'http://localhost:8001' 
    : `${window.location.protocol}//${window.location.host}`;
  
  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      toast({ title: 'Copied!', description: 'Command copied to clipboard' });
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to copy', variant: 'destructive' });
    }
  };
  
  return (
    <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
      <h4 className="font-medium text-blue-300 text-sm mb-3">Quick Install (API Key Method)</h4>
      
      <div className="space-y-2 text-xs text-[#B0B8C4] mb-4">
        <p className="flex items-start gap-2">
          <span className="text-[#3B82F6] font-medium">1.</span>
          Generate an API Key in the Connector tab after creating this operator
        </p>
        <p className="flex items-start gap-2">
          <span className="text-[#3B82F6] font-medium">2.</span>
          Install Python &amp; dependencies, download the connector
        </p>
        <p className="flex items-start gap-2">
          <span className="text-[#3B82F6] font-medium">3.</span>
          Run with your API key
        </p>
      </div>
      
      {/* OS Tabs */}
      <div className="flex gap-1 mb-3">
        <button
          onClick={() => setActiveTab('windows')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            activeTab === 'windows'
              ? 'bg-blue-500/20 text-blue-300'
              : 'bg-[#1E2836] text-[#B0B8C4] hover:text-[#F0F4F8]'
          }`}
        >
          Windows
        </button>
        <button
          onClick={() => setActiveTab('mac')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            activeTab === 'mac'
              ? 'bg-blue-500/20 text-blue-300'
              : 'bg-[#1E2836] text-[#B0B8C4] hover:text-[#F0F4F8]'
          }`}
        >
          Mac / Linux
        </button>
      </div>
      
      {/* Windows Commands */}
      {activeTab === 'windows' && (
        <div className="space-y-3">
          <div>
            <p className="text-xs text-[#7D8896] mb-1">Install dependencies (PowerShell):</p>
            <div className="relative">
              <div className="bg-[#1F2937] rounded-lg p-3 pr-12 font-mono text-xs text-blue-300 overflow-x-auto">
                py -m pip install requests
              </div>
              <button
                onClick={() => handleCopy('py -m pip install requests', 'win-pip')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-[#1A2030] transition-colors"
                title="Copy command"
              >
                {copied === 'win-pip' ? (
                  <Check className="w-4 h-4 text-[#00E5C0]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#B0B8C4]" />
                )}
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs text-[#7D8896] mb-1">Download connector:</p>
            <div className="relative">
              <div className="bg-[#1F2937] rounded-lg p-3 pr-12 font-mono text-xs text-blue-300 overflow-x-auto whitespace-pre-wrap break-all">
                {`Invoke-WebRequest -Uri "${apiBase}/api/exec/connector/connector.py" -OutFile "connector_v3.py"`}
              </div>
              <button
                onClick={() => handleCopy(`Invoke-WebRequest -Uri "${apiBase}/api/exec/connector/connector.py" -OutFile "connector_v3.py"`, 'win-dl')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-[#1A2030] transition-colors"
                title="Copy command"
              >
                {copied === 'win-dl' ? (
                  <Check className="w-4 h-4 text-[#00E5C0]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#B0B8C4]" />
                )}
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs text-[#7D8896] mb-1">Run with API key:</p>
            <div className="relative">
              <div className="bg-[#1F2937] rounded-lg p-3 pr-12 font-mono text-xs text-blue-300 overflow-x-auto">
                py connector_v3.py --key YOUR_API_KEY
              </div>
              <button
                onClick={() => handleCopy('py connector_v3.py --key YOUR_API_KEY', 'win-run')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-[#1A2030] transition-colors"
                title="Copy command"
              >
                {copied === 'win-run' ? (
                  <Check className="w-4 h-4 text-[#00E5C0]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#B0B8C4]" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Mac/Linux Commands */}
      {activeTab === 'mac' && (
        <div className="space-y-3">
          <div>
            <p className="text-xs text-[#7D8896] mb-1">Install dependencies (Terminal):</p>
            <div className="relative">
              <div className="bg-[#1F2937] rounded-lg p-3 pr-12 font-mono text-xs text-[#00E5C0] overflow-x-auto">
                pip3 install requests
              </div>
              <button
                onClick={() => handleCopy('pip3 install requests', 'mac-pip')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-[#1A2030] transition-colors"
                title="Copy command"
              >
                {copied === 'mac-pip' ? (
                  <Check className="w-4 h-4 text-[#00E5C0]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#B0B8C4]" />
                )}
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs text-[#7D8896] mb-1">Download connector:</p>
            <div className="relative">
              <div className="bg-[#1F2937] rounded-lg p-3 pr-12 font-mono text-xs text-[#00E5C0] overflow-x-auto">
                {`curl -sL ${apiBase}/api/exec/connector/connector.py -o connector_v3.py`}
              </div>
              <button
                onClick={() => handleCopy(`curl -sL ${apiBase}/api/exec/connector/connector.py -o connector_v3.py`, 'mac-dl')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-[#1A2030] transition-colors"
                title="Copy command"
              >
                {copied === 'mac-dl' ? (
                  <Check className="w-4 h-4 text-[#00E5C0]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#B0B8C4]" />
                )}
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs text-[#7D8896] mb-1">Run with API key:</p>
            <div className="relative">
              <div className="bg-[#1F2937] rounded-lg p-3 pr-12 font-mono text-xs text-[#00E5C0] overflow-x-auto">
                python3 connector_v3.py --key YOUR_API_KEY
              </div>
              <button
                onClick={() => handleCopy('python3 connector_v3.py --key YOUR_API_KEY', 'mac-run')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-[#1A2030] transition-colors"
                title="Copy command"
              >
                {copied === 'mac-run' ? (
                  <Check className="w-4 h-4 text-[#00E5C0]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#B0B8C4]" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <p className="text-xs text-[#7D8896] mt-3">
        {activeTab === 'windows' 
          ? 'Requires Python 3.8+. Install from python.org if not available.'
          : 'For macOS and Linux. Python 3.8+ required.'
        }
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CREATE OPERATOR MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const CreateOperatorModal = ({ isOpen, onClose, onSuccess, orgId, operator = null, navigateToOperator = null }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [connectorLoading, setConnectorLoading] = useState(false);
  const [availableConnectors, setAvailableConnectors] = useState([]);
  const [pairingCode, setPairingCode] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [acknowledgedRequirements, setAcknowledgedRequirements] = useState(false);
  const [dontShowIntroAgain, setDontShowIntroAgain] = useState(false);
  
  // NEW: API Key connector state
  const [connectorKeys, setConnectorKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [telegramBotUsername, setTelegramBotUsername] = useState(null);
  
  const isEditMode = !!operator;
  
  // Check if user has previously chosen to skip intro
  const skipIntro = localStorage.getItem('operator_skip_intro') === 'true';
  const totalSteps = (isEditMode || skipIntro) ? 7 : 8;
  const [step, setStep] = useState((isEditMode || skipIntro) ? 1 : 0);
  
  const initialFormData = {
    name: '',
    description: '',
    system_prompt: '',
    personality: 'professional',
    memory_enabled: true,
    require_approval_for_destructive: true,
    auto_approve_read_only: true,
    capabilities: [],
    // Connector settings
    connector_id: null,
    connector_mode: 'existing', // 'existing' | 'new' | 'skip'
    // Chat settings
    chat_provider: 'none', // 'none' | 'telegram' | 'whatsapp'
    telegram_bot_token: '',
    whatsapp_phone_number_id: '',
    whatsapp_business_account_id: '',
    whatsapp_access_token: '',
    // Proactive Monitoring settings
    proactive_disk_space: true,
    proactive_system_health: true,
    proactive_email_summary: false,
    proactive_calendar_prep: false,
    // LLM settings
    use_org_llm_key: true,
    llm_provider: 'openai',
    llm_model: '',
    llm_api_key: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);
  
  // Track unsaved changes
  useEffect(() => {
    if (isEditMode && operator) {
      // In edit mode, compare with operator data
      const hasChanges = 
        formData.name !== (operator.name || '') ||
        formData.description !== (operator.description || '') ||
        formData.system_prompt !== (operator.system_prompt || '') ||
        formData.personality !== (operator.personality || 'professional');
      setHasUnsavedChanges(hasChanges);
    } else {
      // In create mode, check if any data entered (only after intro step)
      setHasUnsavedChanges(step > 0 && (formData.name.trim() !== '' || formData.description.trim() !== ''));
    }
  }, [formData, operator, isEditMode, step]);

  // Reset step when modal opens (handles edit mode and skip intro)
  useEffect(() => {
    if (isOpen) {
      const skipIntro = localStorage.getItem('operator_skip_intro') === 'true';
      setStep((!!operator || skipIntro) ? 1 : 0);
      setAcknowledgedRequirements(false);
    }
  }, [isOpen, operator]);
  
  // Initialize form data when modal opens with operator (for edit mode)
  // This ensures form data is reset correctly when reopening the modal
  useEffect(() => {
    if (isOpen && operator) {
      // Determine chat provider based on token status
      let chatProvider = 'none';
      if (operator.has_telegram_token || operator.telegram_bot_token) {
        chatProvider = 'telegram';
      } else if (operator.has_whatsapp_token || operator.whatsapp_access_token) {
        chatProvider = 'whatsapp';
      }
      
      setFormData({
        name: operator.name || '',
        description: operator.description || '',
        system_prompt: operator.system_prompt || '',
        personality: operator.personality || 'professional',
        memory_enabled: operator.memory_enabled ?? true,
        require_approval_for_destructive: operator.require_approval_for_destructive ?? true,
        auto_approve_read_only: operator.auto_approve_read_only ?? true,
        capabilities: operator.capabilities || [],
        connector_id: operator.connector_id || null,
        connector_mode: operator.connector_id ? 'existing' : 'skip',
        chat_provider: chatProvider,
        telegram_bot_token: '', // Don't prefill - security
        whatsapp_phone_number_id: operator.whatsapp_phone_number_id || '',
        whatsapp_business_account_id: operator.whatsapp_business_account_id || '',
        whatsapp_access_token: '', // Don't prefill - security
        // Track if tokens exist (for UI display)
        has_telegram_token: operator.has_telegram_token || false,
        has_whatsapp_token: operator.has_whatsapp_token || false,
        // Proactive Monitoring settings
        proactive_disk_space: operator.proactive_disk_space ?? true,
        proactive_system_health: operator.proactive_system_health ?? true,
        proactive_email_summary: operator.proactive_email_summary ?? false,
        proactive_calendar_prep: operator.proactive_calendar_prep ?? false,
        // LLM settings
        use_org_llm_key: operator.use_org_llm_key ?? true,
        llm_provider: operator.llm_provider || 'openai',
        llm_model: operator.llm_model || '',
        llm_api_key: '', // Don't prefill - security
        has_llm_api_key: operator.has_llm_api_key || false,
      });
    } else if (isOpen && !operator) {
      // Reset to initial form data for new operator
      setFormData(initialFormData);
    }
  }, [isOpen, operator]);

  // Load available connectors when modal opens
  useEffect(() => {
    if (isOpen && orgId) {
      loadConnectors();
    }
  }, [isOpen, orgId]);

  const loadConnectors = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector`,
        {},
        orgId
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableConnectors(data.connectors || []);
      }
    } catch (err) {
      console.error('Failed to load connectors:', err);
    }
  };

  // Fetch connector keys when editing an operator
  useEffect(() => {
    const fetchConnectorKeysForOperator = async () => {
      if (!isEditMode || !operator?.id) return;
      
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/exec/connector/keys?agent_id=${operator.id}`,
          { method: 'GET' },
          orgId
        );
        
        if (response.ok) {
          const data = await response.json();
          setConnectorKeys(data.keys || []);
        }
      } catch (err) {
        console.error('Failed to fetch connector keys:', err);
      }
    };
    
    fetchConnectorKeysForOperator();
  }, [isEditMode, operator?.id, orgId]);

  // Fetch telegram bot username when editing an operator with telegram configured
  useEffect(() => {
    const fetchTelegramStatus = async () => {
      if (!isEditMode || !operator?.id || !operator?.has_telegram_token) return;
      
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/exec/chat/telegram/status/${operator.id}`,
          { method: 'GET' },
          orgId
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.configured && data.bot_username) {
            setTelegramBotUsername(data.bot_username);
          }
        }
      } catch (err) {
        console.error('Failed to fetch telegram status:', err);
      }
    };
    
    fetchTelegramStatus();
  }, [isEditMode, operator?.id, operator?.has_telegram_token, orgId]);

  const personalities = [
    { id: 'professional', label: 'Professional', desc: 'Formal and business-focused' },
    { id: 'friendly', label: 'Friendly', desc: 'Warm and approachable' },
    { id: 'concise', label: 'Concise', desc: 'Brief and to the point' },
    { id: 'detailed', label: 'Detailed', desc: 'Thorough explanations' },
  ];

  const capabilitiesOptions = [
    { id: 'file_operations', label: 'File Operations', desc: 'Read, write, and manage files' },
    { id: 'browser_automation', label: 'Browser Automation', desc: 'Navigate and interact with web pages' },
    { id: 'shell_commands', label: 'Shell Commands', desc: 'Execute terminal commands' },
    { id: 'api_calls', label: 'API Calls', desc: 'Make HTTP requests to external APIs' },
  ];

  // Create a new connector and get pairing code
  const handleCreateConnector = async () => {
    setConnectorLoading(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: `${formData.name} Connector`,
            device_name: 'Pending Setup'
          })
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        setPairingCode(data.pairing_code);
        setFormData(prev => ({ ...prev, connector_id: data.connector?.id }));
        toast({ title: 'Success', description: 'Connector created! Use the pairing code below.' });
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to create connector', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to create connector', variant: 'destructive' });
    } finally {
      setConnectorLoading(false);
    }
  };

  // Trash a connector
  const handleDeleteConnector = async (connectorId, e) => {
    e.stopPropagation(); // Prevent selecting the connector when clicking delete
    
    if (!confirm('Are you sure you want to delete this connector? This will revoke access and disconnect any connected device.')) {
      return;
    }
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/${connectorId}`,
        { method: 'DELETE' },
        orgId
      );
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Connector deleted successfully' });
        // ArrowsClockwise connectors list
        loadConnectors();
        // Clear selection if this connector was selected
        if (formData.connector_id === connectorId) {
          setFormData(prev => ({ ...prev, connector_id: null }));
        }
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to delete connector', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete connector', variant: 'destructive' });
    }
  };

  // Generate API Key for connector (when editing existing operator)
  const handleGenerateApiKey = async () => {
    if (!isEditMode || !operator?.id) {
      toast({ title: 'Error', description: 'Please save the operator first', variant: 'destructive' });
      return;
    }
    
    setGeneratingKey(true);
    setNewlyGeneratedKey(null);
    
    try {
      const keyName = newKeyName.trim() || `Connector for ${formData.name || operator.name}`;
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/key/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agent_id: operator.id,
            name: keyName
          })
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        setNewlyGeneratedKey(data.api_key);
        setNewKeyName('');
        
        // ArrowsClockwise the keys list
        const keysResponse = await fetchWithOrg(
          `${BACKEND_URL}/api/exec/connector/keys?agent_id=${operator.id}`,
          { method: 'GET' },
          orgId
        );
        if (keysResponse.ok) {
          const keysData = await keysResponse.json();
          setConnectorKeys(keysData.keys || []);
        }
        
        toast({ 
          title: 'API Key Generated!', 
          description: 'Save this key securely - it won\'t be shown again.' 
        });
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to generate key', variant: 'destructive' });
      }
    } catch (err) {
      console.error('Generate key error:', err);
      toast({ title: 'Error', description: 'Failed to generate API key', variant: 'destructive' });
    } finally {
      setGeneratingKey(false);
    }
  };

  // Copy API key to clipboard
  const handleCopyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(true);
      toast({ title: 'Copied!', description: 'API key copied to clipboard' });
      setTimeout(() => setCopiedKey(false), 3000);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to copy to clipboard', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Please enter a name for your operator', variant: 'destructive' });
      return;
    }

    try {
      setLoading(true);
      
      const url = isEditMode 
        ? `${BACKEND_URL}/api/exec/agents/${operator.id}`
        : `${BACKEND_URL}/api/exec/agents`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetchWithOrg(
        url,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        toast({ 
          title: 'Success', 
          description: isEditMode 
            ? `Operator "${formData.name}" updated successfully!`
            : `Operator "${formData.name}" created successfully!` 
        });
        onSuccess();
        resetForm();
        
        // Navigate to the operator detail page for new operators
        if (!isEditMode && navigateToOperator && data.agent?.id) {
          navigateToOperator(data.agent.id);
        } else if (!isEditMode && data.id && navigateToOperator) {
          navigateToOperator(data.id);
        }
      } else {
        const error = await response.json();
        // Handle both string and object error details
        const errorMessage = typeof error.detail === 'object' 
          ? error.detail.message || `Failed to ${isEditMode ? 'update' : 'create'} operator`
          : error.detail || `Failed to ${isEditMode ? 'update' : 'create'} operator`;
        toast({ 
          title: `${isEditMode ? 'Update' : 'Creation'} Failed`, 
          description: errorMessage,
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error(`${isEditMode ? 'Update' : 'Create'} error:`, err);
      toast({ title: 'Error', description: `Failed to ${isEditMode ? 'update' : 'create'} operator`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const skipIntro = localStorage.getItem('operator_skip_intro') === 'true';
    setStep((isEditMode || skipIntro) ? 1 : 0);
    setPairingCode(null);
    setHasUnsavedChanges(false);
    setShowDiscardConfirm(false);
    setAcknowledgedRequirements(false);
    setFormData(initialFormData);
  };

  // Handle proceeding from intro step
  const handleProceedFromIntro = () => {
    if (dontShowIntroAgain) {
      localStorage.setItem('operator_skip_intro', 'true');
    }
    setStep(1);
  };

  // Handle save as draft (creates/updates with draft status)
  const handleSaveDraft = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Please enter a name before saving', variant: 'destructive' });
      return;
    }

    try {
      setSavingDraft(true);
      
      const url = isEditMode 
        ? `${BACKEND_URL}/api/exec/agents/${operator.id}`
        : `${BACKEND_URL}/api/exec/agents`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      // Save with draft status
      const draftData = { ...formData, status: 'draft' };
      
      const response = await fetchWithOrg(
        url,
        {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(draftData)
        },
        orgId
      );
      
      if (response.ok) {
        toast({ 
          title: 'Draft Saved', 
          description: `"${formData.name}" saved as draft. You can continue editing anytime.` 
        });
        setHasUnsavedChanges(false);
        onSuccess();
        resetForm();
      } else {
        const error = await response.json();
        const errorMessage = typeof error.detail === 'object' 
          ? error.detail.message || 'Failed to save draft'
          : error.detail || 'Failed to save draft';
        toast({ 
          title: 'Save Failed', 
          description: errorMessage,
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Save draft error:', err);
      toast({ title: 'Error', description: 'Failed to save draft', variant: 'destructive' });
    } finally {
      setSavingDraft(false);
    }
  };

  // Handle close with confirmation if unsaved changes
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      resetForm();
      onClose();
    }
  };

  // Confirm discard changes
  const handleDiscard = () => {
    setShowDiscardConfirm(false);
    resetForm();
    onClose();
  };

  const toggleCapability = (capId) => {
    setFormData(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(capId)
        ? prev.capabilities.filter(c => c !== capId)
        : [...prev.capabilities, capId]
    }));
  };

  const getStepTitle = (stepNum) => {
    // If showing intro (step 0), different title structure
    if (!isEditMode && !skipIntro) {
      const titles = {
        0: 'Getting Started',
        1: 'Basic Information',
        2: 'Personality',
        3: 'Capabilities & Safety',
        4: 'Connect Device',
        5: 'Chat Integration',
        6: 'Proactive Monitoring',
        7: 'LLM Configuration'
      };
      return titles[stepNum] || '';
    }
    // Edit mode or skip intro - original steps
    const titles = {
      1: 'Basic Information',
      2: 'Personality',
      3: 'Capabilities & Safety',
      4: 'Connect Device',
      5: 'Chat Integration',
      6: 'Proactive Monitoring',
      7: 'LLM Configuration'
    };
    return titles[stepNum] || '';
  };

  // Get display step number (for progress indicator)
  const getDisplayStep = () => {
    if (!isEditMode && !skipIntro) {
      return step + 1; // Step 0 becomes "Step 1 of 8"
    }
    return step; // Normal 1-7
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - no onClick to prevent accidental closure */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Discard Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 max-w-md mx-4 shadow-2xl"
          >
            <h3 className="text-lg font-semibold text-white mb-2">Unsaved Changes</h3>
            <p className="text-[#B0B8C4] mb-6">
              You have unsaved changes. Would you like to save as a draft before leaving?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                className="px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] text-white transition-colors"
              >
                Continue Editing
              </button>
              <button
                onClick={handleDiscard}
                className="px-4 py-2 rounded-lg bg-red-600/20 text-[#FF6B6B] hover:bg-red-600/30 transition-colors"
              >
                Discard
              </button>
              <button
                onClick={() => {
                  setShowDiscardConfirm(false);
                  handleSaveDraft();
                }}
                disabled={savingDraft || !formData.name.trim()}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] hover:opacity-90 text-white transition-opacity disabled:opacity-50"
              >
                {savingDraft ? 'Saving...' : 'Save Draft'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-auto bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#1F2937] border-b border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${isEditMode ? 'from-blue-500 to-cyan-600' : 'from-[#00E5C0] to-red-500'} flex items-center justify-center`}>
                {isEditMode ? <GearSix className="w-5 h-5 text-white" /> : <Cpu className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">{isEditMode ? 'Edit Operator' : 'Create Operator'}</h2>
                <p className="text-sm text-[#B0B8C4]">Step {getDisplayStep()} of {totalSteps} — {getStepTitle(step)}</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors">
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
          
          {/* Progress */}
          <div className="flex gap-1 mt-4">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map(s => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  s <= getDisplayStep() ? (isEditMode ? 'bg-blue-500' : 'bg-gradient-to-r from-[#00E5C0] to-red-500') : 'bg-[#1A2030]'
                }`}
              />
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {/* Step 0: Introduction / Getting Started (only for new operators, unless skipped) */}
            {step === 0 && !isEditMode && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* What is an Operator Agent */}
                <div className="bg-gradient-to-br from-[#00E5C0]/10 to-purple-500/10 border border-[#00E5C0]/20 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00E5C0] to-[#00CCB0] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img src="/orion-icon-192.png" alt="Orion" className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-2">What is Orion - Operator Agent?</h3>
                      <p className="text-sm text-[#F0F4F8] leading-relaxed">
                        Orion is an AI assistant that runs tasks directly on your local machine through a secure connector. 
                        It can automate file operations, browser tasks, shell commands, and API calls — all while keeping you in control.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Safety & Security Features */}
                <div className="bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
                  <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#00E5C0]" />
                    Safety & Security Features
                  </h3>
                  <div className="grid gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Approval Required</p>
                        <p className="text-xs text-[#B0B8C4]">Destructive actions always need your explicit permission before executing</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <Lock className="w-4 h-4 text-[#3B82F6]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Scoped Access</p>
                        <p className="text-xs text-[#B0B8C4]">Only accesses folders, apps, and domains you explicitly allow</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#00E5C0]/20 flex items-center justify-center flex-shrink-0">
                        <Eye className="w-4 h-4 text-[#00E5C0]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Full Audit Log</p>
                        <p className="text-xs text-[#B0B8C4]">Every action is logged and reviewable in your dashboard</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#FF6B6B]/20 flex items-center justify-center flex-shrink-0">
                        <Lightning className="w-4 h-4 text-[#FF6B6B]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Kill Switch</p>
                        <p className="text-xs text-[#B0B8C4]">Instantly disconnect and remove all components from your device. Revokes all authentication tokens and uninstalls the connector completely.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Requirements */}
                <div className="bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
                  <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                    <Terminal className="w-5 h-5 text-cyan-400" />
                    System Requirements
                  </h3>
                  <div className="grid gap-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#00E5C0] flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-white">
                          <span className="font-medium">Python 3.8+</span>
                          <span className="text-[#B0B8C4]"> — </span>
                          <a 
                            href="https://python.org/downloads" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                          >
                            Download from python.org
                          </a>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#00E5C0] flex-shrink-0" />
                      <p className="text-sm text-white">
                        <span className="font-medium">Operating System:</span>
                        <span className="text-[#B0B8C4]"> Windows 10+, macOS 10.15+, or Linux (Ubuntu 18.04+)</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#00E5C0] flex-shrink-0" />
                      <p className="text-sm text-white">
                        <span className="font-medium">Network:</span>
                        <span className="text-[#B0B8C4]"> Stable internet connection (outbound HTTPS only)</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#00E5C0] flex-shrink-0" />
                      <p className="text-sm text-white">
                        <span className="font-medium">Disk Space:</span>
                        <span className="text-[#B0B8C4]"> ~100MB for connector and dependencies</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <WarningCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <p className="text-sm text-amber-200">
                        Device must stay <span className="font-medium">online and awake</span> while operator is active
                      </p>
                    </div>
                  </div>
                </div>

                {/* Acknowledgment */}
                <div className="bg-[#1E2836]/30 border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acknowledgedRequirements}
                      onChange={(e) => setAcknowledgedRequirements(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-zinc-600 bg-[#1A2030] text-pink-500 focus:ring-pink-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-sm text-[#F0F4F8]">
                      I understand the requirements and safety features of Orion - Operator Agents
                    </span>
                  </label>
                </div>

                {/* Don't show again */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer text-[#7D8896] hover:text-[#B0B8C4] transition-colors">
                    <input
                      type="checkbox"
                      checked={dontShowIntroAgain}
                      onChange={(e) => setDontShowIntroAgain(e.target.checked)}
                      className="w-4 h-4 rounded border-zinc-600 bg-[#1A2030] text-[#7D8896] focus:ring-zinc-500 focus:ring-offset-0 cursor-pointer"
                    />
                    <span className="text-xs">Don&apos;t show this intro again</span>
                  </label>
                </div>
              </motion.div>
            )}
            
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-base font-medium text-white mb-4">Basic Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                        Operator Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., DevOps Assistant"
                        className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-[#00E5C0] focus:outline-none text-white placeholder:text-[#7D8896]"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="What does this operator do?"
                        rows={3}
                        className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-[#00E5C0] focus:outline-none text-white placeholder:text-[#7D8896] resize-none"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-base font-medium text-white mb-4">Personality & Instructions</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                        Personality Style
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {personalities.map(p => (
                          <button
                            key={p.id}
                            onClick={() => setFormData({ ...formData, personality: p.id })}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              formData.personality === p.id
                                ? 'border-[#00E5C0] bg-[#00E5C0]/10'
                                : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                            }`}
                          >
                            <p className="font-medium text-sm text-white">{p.label}</p>
                            <p className="text-xs text-[#B0B8C4]">{p.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                        System Instructions
                      </label>
                      <textarea
                        value={formData.system_prompt}
                        onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                        placeholder="Custom instructions for this operator..."
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-[#00E5C0] focus:outline-none text-white placeholder:text-[#7D8896] resize-none"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-base font-medium text-white mb-4">Capabilities & Safety</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                        Enabled Capabilities
                      </label>
                      <p className="text-xs text-[#7D8896] mb-3">Click to toggle each capability on or off</p>
                      <div className="space-y-2">
                        {capabilitiesOptions.map(cap => (
                          <button
                            key={cap.id}
                            onClick={() => toggleCapability(cap.id)}
                            className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-center justify-between group cursor-pointer ${
                              formData.capabilities.includes(cap.id)
                                ? 'border-[#00E5C0] bg-[#00E5C0]/15 shadow-[0_0_15px_rgba(236,72,153,0.15)]'
                                : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-500 hover:bg-[#1E2836]'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                formData.capabilities.includes(cap.id)
                                  ? 'border-[#00E5C0] bg-[#00E5C0]'
                                  : 'border-zinc-500 group-hover:border-zinc-400'
                              }`}>
                                {formData.capabilities.includes(cap.id) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-sm text-white">{cap.label}</p>
                                <p className="text-xs text-[#B0B8C4]">{cap.desc}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-[rgba(255,255,255,0.08)]">
                      <label className="block text-sm font-medium text-[#F0F4F8] mb-3">
                        Safety GearSix
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center justify-between p-3 rounded-lg bg-[#1E2836]/50 cursor-pointer">
                          <div>
                            <p className="text-sm font-medium text-white">Enable Memory</p>
                            <p className="text-xs text-[#B0B8C4]">Operator remembers context across sessions</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.memory_enabled}
                            onChange={(e) => setFormData({ ...formData, memory_enabled: e.target.checked })}
                            className="w-5 h-5 rounded border-zinc-600 bg-[#1A2030] text-pink-500 focus:ring-pink-500"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between p-3 rounded-lg bg-[#1E2836]/50 cursor-pointer">
                          <div>
                            <p className="text-sm font-medium text-white">Require Approval for Destructive Actions</p>
                            <p className="text-xs text-[#B0B8C4]">Manual approval needed for file changes</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.require_approval_for_destructive}
                            onChange={(e) => setFormData({ ...formData, require_approval_for_destructive: e.target.checked })}
                            className="w-5 h-5 rounded border-zinc-600 bg-[#1A2030] text-pink-500 focus:ring-pink-500"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between p-3 rounded-lg bg-[#1E2836]/50 cursor-pointer">
                          <div>
                            <p className="text-sm font-medium text-white">Auto-Approve Read-Only Operations</p>
                            <p className="text-xs text-[#B0B8C4]">Automatically approve safe operations</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.auto_approve_read_only}
                            onChange={(e) => setFormData({ ...formData, auto_approve_read_only: e.target.checked })}
                            className="w-5 h-5 rounded border-zinc-600 bg-[#1A2030] text-pink-500 focus:ring-pink-500"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Connect Device (API Key Method) */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-base font-medium text-white mb-2">Connect a Device</h3>
                  <p className="text-sm text-[#B0B8C4] mb-6">
                    Generate an API key to connect your local machine to this operator.
                  </p>
                  
                  {/* Show existing connection status in edit mode */}
                  {isEditMode && connectorKeys.filter(k => k.active).length > 0 && (
                    <div className="mb-6 p-4 rounded-lg bg-[#00E5C0]/10 border border-green-500/30">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-[#00E5C0] font-medium">Device Connected</span>
                      </div>
                      <div className="space-y-2">
                        {connectorKeys.filter(k => k.active).map(key => (
                          <div key={key.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-white">{key.name}</span>
                              <span className="text-[#7D8896] font-mono text-xs">{key.key_prefix}...</span>
                            </div>
                            <div className="flex items-center gap-3 text-[#B0B8C4] text-xs">
                              {key.device_name && <span>{key.device_name}</span>}
                              {key.last_used_at && (
                                <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-[#B0B8C4] mt-3">
                        Manage keys from the <strong className="text-white">Connector tab</strong> after saving.
                      </p>
                    </div>
                  )}
                  
                  {/* For NEW operators - show info message */}
                  {!isEditMode && (
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                        <div className="flex items-start gap-3">
                          <WarningCircle className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-[#3B82F6] font-medium text-sm mb-1">Save Your Operator First</p>
                            <p className="text-[#B0B8C4] text-sm">
                              Complete the wizard and save your operator. Then you can generate an API key from the <strong className="text-white">Connector tab</strong>.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <QuickInstallCard />
                    </div>
                  )}
                  
                  {/* For EXISTING operators without keys - allow generating */}
                  {isEditMode && connectorKeys.filter(k => k.active).length === 0 && (
                    <div className="space-y-4">
                      {/* Newly generated key display */}
                      {newlyGeneratedKey && (
                        <div className="p-4 rounded-lg bg-[#00E5C0]/10 border border-green-500/30">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-5 h-5 text-[#00E5C0]" />
                            <span className="text-[#00E5C0] font-medium">API Key Generated!</span>
                          </div>
                          <p className="text-xs text-amber-400 mb-3">
                            ⚠️ Save this key now - it won&apos;t be shown again!
                          </p>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 p-3 rounded bg-[#1F2937] text-[#00E5C0] font-mono text-xs break-all">
                              {newlyGeneratedKey}
                            </code>
                            <button
                              onClick={() => handleCopyKey(newlyGeneratedKey)}
                              className="p-3 rounded bg-[#1E2836] hover:bg-[#1A2030] text-white transition-colors"
                            >
                              {copiedKey ? <CheckCircle size={16} /> : <Copy size={16} />}
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Key generation form */}
                      {!newlyGeneratedKey && (
                        <>
                          <div className="p-4 rounded-lg bg-[#00E5C0]/5 border border-[#00E5C0]/20">
                            <h4 className="font-medium text-[#00E5C0] text-sm mb-2">Generate API Key</h4>
                            <p className="text-xs text-[#B0B8C4] mb-4">
                              Create a secure API key to connect your local machine to this operator.
                            </p>
                            
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                placeholder={`Connector for ${formData.name || 'Operator'}`}
                                className="flex-1 px-3 py-2 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-[#00E5C0] focus:outline-none text-white placeholder:text-[#7D8896] text-sm"
                              />
                              <button
                                onClick={handleGenerateApiKey}
                                disabled={generatingKey}
                                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#00E5C0] to-rose-500 hover:opacity-90 text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50"
                              >
                                {generatingKey ? (
                                  <CircleNotch size={16} className="animate-spin" />
                                ) : (
                                  <Lightning size={16} />
                                )}
                                Generate Key
                              </button>
                            </div>
                          </div>
                          
                          <QuickInstallCard />
                        </>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 5: Chat Integration */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-base font-medium text-white mb-2">Chat Integration</h3>
                  <p className="text-sm text-[#B0B8C4] mb-6">
                    Connect a chat platform to interact with this operator via messaging.
                  </p>
                  
                  {/* Chat Provider Selection */}
                  <div className="space-y-3 mb-6">
                    <button
                      onClick={() => setFormData({ ...formData, chat_provider: 'none', telegram_bot_token: '', whatsapp_access_token: '' })}
                      className={`w-full p-4 rounded-lg border text-left transition-all ${
                        formData.chat_provider === 'none'
                          ? 'border-[#00E5C0] bg-[#00E5C0]/10'
                          : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-[#B0B8C4]" />
                        <div>
                          <p className="font-medium text-white">No Chat Integration</p>
                          <p className="text-xs text-[#B0B8C4]">Skip chat setup for now</p>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setFormData({ ...formData, chat_provider: 'telegram' })}
                      className={`w-full p-4 rounded-lg border text-left transition-all ${
                        formData.chat_provider === 'telegram'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <PaperPlaneRight className="w-5 h-5 text-[#3B82F6]" />
                          <div>
                            <p className="font-medium text-white">Telegram</p>
                            <p className="text-xs text-[#B0B8C4]">Chat via Telegram bot</p>
                          </div>
                        </div>
                        {/* Show configured status */}
                        {(formData.has_telegram_token || formData.telegram_bot_token) && (
                          <span className="px-2 py-0.5 text-xs rounded bg-[#00E5C0]/20 text-[#00E5C0]">
                            Configured
                          </span>
                        )}
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setFormData({ ...formData, chat_provider: 'whatsapp' })}
                      className={`w-full p-4 rounded-lg border text-left transition-all ${
                        formData.chat_provider === 'whatsapp'
                          ? 'border-green-500 bg-[#00E5C0]/10'
                          : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <ChatText className="w-5 h-5 text-[#00E5C0]" />
                          <div>
                            <p className="font-medium text-white">WhatsApp</p>
                            <p className="text-xs text-[#B0B8C4]">Chat via WhatsApp Business API</p>
                          </div>
                        </div>
                        {/* Show configured status */}
                        {(formData.has_whatsapp_token || formData.whatsapp_access_token) && (
                          <span className="px-2 py-0.5 text-xs rounded bg-[#00E5C0]/20 text-[#00E5C0]">
                            Configured
                          </span>
                        )}
                      </div>
                    </button>
                  </div>
                  
                  {/* Telegram Configuration */}
                  {formData.chat_provider === 'telegram' && (
                    <div className="space-y-4">
                      {/* Show existing token status with pairing link */}
                      {isEditMode && formData.has_telegram_token && !formData.telegram_bot_token && (
                        <div className="space-y-4">
                          <div className="p-4 rounded-lg bg-[#00E5C0]/10 border border-green-500/30">
                            <div className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full bg-green-400" />
                              <span className="text-[#00E5C0] font-medium text-sm">Telegram Robot Connected</span>
                            </div>
                            <p className="text-xs text-[#B0B8C4] mt-2">
                              Your bot token is configured. Now connect your Telegram chat below.
                            </p>
                          </div>
                          
                          {/* Pairing Instructions - Manual Method Primary */}
                          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                            <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                              <PaperPlaneRight size={16} className="text-[#3B82F6]" />
                              Connect Your Telegram Chat
                            </h4>
                            <p className="text-xs text-[#B0B8C4] mb-3">
                              Send this command in Telegram to connect:
                            </p>
                            
                            {/* Copy Command Section */}
                            <div className="flex items-center gap-2 mb-3">
                              <code className="flex-1 px-3 py-2 rounded bg-[#1F2937] text-[#00E5C0] font-mono text-sm">
                                /start {operator?.id}
                              </code>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(`/start ${operator?.id}`);
                                  toast.success('Command copied!');
                                }}
                                className="px-3 py-2 rounded bg-[#1A2030] hover:bg-zinc-600 text-white transition-colors"
                              >
                                <Copy size={14} />
                              </button>
                            </div>
                            
                            <ol className="text-xs text-[#B0B8C4] space-y-1">
                              <li>1. Open Telegram → search your bot: <span className="text-[#3B82F6]">{telegramBotUsername ? `@${telegramBotUsername}` : '(your bot name)'}</span></li>
                              <li>2. Paste and send the command above</li>
                              <li>3. Robot confirms: &quot;✅ Successfully connected!&quot;</li>
                            </ol>
                            
                            {/* Alternative Link (collapsed) */}
                            {telegramBotUsername && (
                            <details className="mt-3">
                              <summary className="text-xs text-[#7D8896] cursor-pointer hover:text-[#F0F4F8]">
                                Or try the quick link →
                              </summary>
                              <a
                                href={`https://t.me/${telegramBotUsername}?start=${operator?.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex px-3 py-2 rounded bg-blue-500/20 text-[#3B82F6] text-xs items-center gap-2"
                              >
                                <PaperPlaneRight size={12} />
                                Open in Telegram
                              </a>
                            </details>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Instructions for getting a Telegram token - ALWAYS SHOWN */}
                      <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                        <h4 className="font-medium text-blue-300 text-sm mb-3">📘 How to Create a Telegram Robot</h4>
                        <ol className="text-xs text-[#B0B8C4] space-y-2">
                          <li className="flex items-start gap-2">
                            <span className="text-[#3B82F6] font-bold">1.</span>
                            <span>Open Telegram and search for <span className="text-blue-300 font-semibold">@BotFather</span></span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#3B82F6] font-bold">2.</span>
                            <span>PaperPlaneRight <code className="px-1 bg-[#1A2030] rounded">/newbot</code> to create a new bot</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#3B82F6] font-bold">3.</span>
                            <span>Follow the prompts to set a name and username for your bot</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#3B82F6] font-bold">4.</span>
                            <span>BotFather will give you an <span className="text-[#00E5C0]">API token</span> - copy it and paste below</span>
                          </li>
                        </ol>
                        <a 
                          href="https://t.me/botfather" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-1 text-xs text-[#3B82F6] hover:text-blue-300"
                        >
                          <ArrowSquareOut size={10} />
                          Open @BotFather in Telegram
                        </a>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                          Robot Token 
                          {formData.has_telegram_token && !formData.telegram_bot_token && (
                            <span className="text-[#00E5C0] text-xs ml-2">● Currently configured</span>
                          )}
                          {formData.telegram_bot_token && (
                            <span className="text-[#3B82F6] text-xs ml-2">● New token entered</span>
                          )}
                        </label>
                        <input
                          type="password"
                          value={formData.telegram_bot_token}
                          onChange={(e) => setFormData({ ...formData, telegram_bot_token: e.target.value })}
                          placeholder={formData.has_telegram_token ? 'Leave empty to keep current token, or enter new token' : '123456789:ABCdefGHIjklMNOpqrsTUVwxyz'}
                          className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-blue-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
                        />
                        {formData.has_telegram_token && (
                          <p className="text-xs text-[#7D8896] mt-1">
                            Leave empty to keep the existing token, or enter a new one to replace it.
                          </p>
                        )}
                      </div>
                      
                      {/* Note about pairing for NEW setups */}
                      {!isEditMode && (
                        <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                          <p className="text-xs text-amber-400">
                            <strong>Next step:</strong> After saving, return to this wizard or go to the <strong className="text-white">Chat tab</strong> to connect your Telegram chat.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* WhatsApp Configuration */}
                  {formData.chat_provider === 'whatsapp' && (
                    <div className="space-y-4">
                      {/* Show existing config status */}
                      {isEditMode && formData.has_whatsapp_token && !formData.whatsapp_access_token && (
                        <div className="p-4 rounded-lg bg-[#00E5C0]/10 border border-green-500/30">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-green-400" />
                            <span className="text-[#00E5C0] font-medium text-sm">WhatsApp Connected</span>
                          </div>
                          <p className="text-xs text-[#B0B8C4] mt-2">
                            Your WhatsApp credentials are configured. Update below only if needed.
                          </p>
                        </div>
                      )}
                      
                      {/* Instructions for getting WhatsApp credentials - ALWAYS SHOWN */}
                      <div className="p-4 rounded-lg bg-[#00E5C0]/5 border border-green-500/20">
                        <h4 className="font-medium text-[#00E5C0] text-sm mb-3">📘 How to Set Up WhatsApp Business API</h4>
                        <ol className="text-xs text-[#B0B8C4] space-y-2">
                          <li className="flex items-start gap-2">
                            <span className="text-[#00E5C0] font-bold">1.</span>
                            <span>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] underline">Meta for Developers</a></span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#00E5C0] font-bold">2.</span>
                            <span>Create a new app and select <span className="text-[#00E5C0]">WhatsApp</span> as the product</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#00E5C0] font-bold">3.</span>
                            <span>Go to <span className="text-white">WhatsApp → API Setup</span> in the dashboard</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#00E5C0] font-bold">4.</span>
                            <span>Copy the <span className="text-[#00E5C0]">Phone Number ID</span>, <span className="text-[#00E5C0]">Business Account ID</span>, and <span className="text-[#00E5C0]">Access Token</span></span>
                          </li>
                        </ol>
                        <a 
                          href="https://developers.facebook.com/apps" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center gap-1 text-xs text-[#00E5C0] hover:text-[#00E5C0]"
                        >
                          <ArrowSquareOut size={10} />
                          Open Meta for Developers
                        </a>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                          Phone Number ID
                        </label>
                        <input
                          type="text"
                          value={formData.whatsapp_phone_number_id}
                          onChange={(e) => setFormData({ ...formData, whatsapp_phone_number_id: e.target.value })}
                          placeholder="123456789012345"
                          className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-green-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                          Business Account ID
                        </label>
                        <input
                          type="text"
                          value={formData.whatsapp_business_account_id}
                          onChange={(e) => setFormData({ ...formData, whatsapp_business_account_id: e.target.value })}
                          placeholder="123456789012345"
                          className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-green-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                          Access Token 
                          {formData.has_whatsapp_token && !formData.whatsapp_access_token && (
                            <span className="text-[#00E5C0] text-xs ml-2">● Currently configured</span>
                          )}
                        </label>
                        <input
                          type="password"
                          value={formData.whatsapp_access_token}
                          onChange={(e) => setFormData({ ...formData, whatsapp_access_token: e.target.value })}
                          placeholder={formData.has_whatsapp_token ? 'Leave empty to keep current token' : 'Your access token'}
                          className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-green-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 6: Proactive Monitoring */}
            {step === 6 && (
              <motion.div
                key="step6"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-base font-medium text-white mb-2 flex items-center gap-2">
                    <Lightning size={18} className="text-pink-500" />
                    Proactive Monitoring
                  </h3>
                  <p className="text-sm text-[#B0B8C4] mb-6">
                    Enable proactive checks to let your operator monitor your system and suggest helpful actions via chat.
                  </p>
                  
                  {/* Available Checks */}
                  <div className="space-y-3">
                    {/* Disk Space Monitor */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/10">
                          <HardDrives size={20} className="text-[#3B82F6]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Disk Space Monitor</p>
                          <p className="text-xs text-[#B0B8C4]">Alert when disk space is running low</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={formData.proactive_disk_space}
                          onChange={(e) => setFormData({ ...formData, proactive_disk_space: e.target.checked })}
                          className="sr-only peer" 
                        />
                        <div className="w-11 h-6 bg-[#1A2030] peer-focus:ring-2 peer-focus:ring-pink-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00E5C0]"></div>
                      </label>
                    </div>

                    {/* System Health Check */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#00E5C0]/10">
                          <Monitor size={20} className="text-[#00E5C0]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">System Health</p>
                          <p className="text-xs text-[#B0B8C4]">Monitor CPU and memory usage</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={formData.proactive_system_health}
                          onChange={(e) => setFormData({ ...formData, proactive_system_health: e.target.checked })}
                          className="sr-only peer" 
                        />
                        <div className="w-11 h-6 bg-[#1A2030] peer-focus:ring-2 peer-focus:ring-pink-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00E5C0]"></div>
                      </label>
                    </div>

                    {/* Email Summary */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#00E5C0]/10">
                          <Envelope size={20} className="text-[#00E5C0]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Email Summary</p>
                          <p className="text-xs text-[#B0B8C4]">Summarize unread emails</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.proactive_email_summary || false}
                          onChange={(e) => setFormData({ ...formData, proactive_email_summary: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#1A2030] peer-focus:ring-2 peer-focus:ring-pink-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00E5C0]"></div>
                      </label>
                    </div>

                    {/* Calendar Prep */}
                    <div className="flex items-center justify-between p-4 rounded-xl bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-500/10">
                          <Calendar size={20} className="text-orange-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Calendar Prep</p>
                          <p className="text-xs text-[#B0B8C4]">Prepare meeting briefings</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.proactive_calendar_prep || false}
                          onChange={(e) => setFormData({ ...formData, proactive_calendar_prep: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#1A2030] peer-focus:ring-2 peer-focus:ring-pink-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00E5C0]"></div>
                      </label>
                    </div>
                  </div>
                  
                  {/* How It Works */}
                  <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-[#00E5C0]/5 to-transparent border border-[#00E5C0]/20">
                    <p className="text-xs text-[#00E5C0] font-medium mb-2">How it works:</p>
                    <ol className="text-xs text-[#B0B8C4] space-y-1">
                      <li>1. Operator monitors your system via the connector</li>
                      <li>2. When something needs attention, you get a suggestion via chat</li>
                      <li>3. Reply &quot;yes&quot; to approve, &quot;no&quot; to skip, or &quot;later&quot; to snooze</li>
                    </ol>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 7: LLM Configuration */}
            {step === 7 && (
              <motion.div
                key="step7"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-base font-medium text-white mb-2">LLM Configuration</h3>
                  <p className="text-sm text-[#B0B8C4] mb-6">
                    Choose how this operator accesses AI language models.
                  </p>
                  
                  {/* LLM Source Selection */}
                  <div className="space-y-3 mb-6">
                    <button
                      onClick={() => setFormData({ ...formData, use_org_llm_key: true, llm_api_key: '' })}
                      className={`w-full p-4 rounded-lg border text-left transition-all ${
                        formData.use_org_llm_key
                          ? 'border-[#00E5C0] bg-[#00E5C0]/10'
                          : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          formData.use_org_llm_key ? 'bg-[#00E5C0]/20' : 'bg-[#1A2030]'
                        }`}>
                          <Shield className={`w-5 h-5 ${formData.use_org_llm_key ? 'text-[#00E5C0]' : 'text-[#B0B8C4]'}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white">Use Organization LLM Key</p>
                          <p className="text-xs text-[#B0B8C4]">
                            Use the LLM API key configured in your organization settings
                          </p>
                        </div>
                        {formData.use_org_llm_key && (
                          <CheckCircle className="w-5 h-5 text-[#00E5C0]" />
                        )}
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setFormData({ ...formData, use_org_llm_key: false })}
                      className={`w-full p-4 rounded-lg border text-left transition-all ${
                        !formData.use_org_llm_key
                          ? 'border-purple-500 bg-[#00E5C0]/10'
                          : 'border-[rgba(255,255,255,0.08)] bg-[#1E2836]/50 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          !formData.use_org_llm_key ? 'bg-[#00E5C0]/20' : 'bg-[#1A2030]'
                        }`}>
                          <Lightning className={`w-5 h-5 ${!formData.use_org_llm_key ? 'text-[#00E5C0]' : 'text-[#B0B8C4]'}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white">Use Custom LLM Key</p>
                          <p className="text-xs text-[#B0B8C4]">
                            Override with a specific API key for this operator only
                          </p>
                        </div>
                        {!formData.use_org_llm_key && (
                          <CheckCircle className="w-5 h-5 text-[#00E5C0]" />
                        )}
                      </div>
                    </button>
                  </div>
                  
                  {/* Custom LLM Configuration */}
                  {!formData.use_org_llm_key && (
                    <div className="space-y-4 p-4 rounded-lg bg-[#00E5C0]/5 border border-purple-500/20">
                      <h4 className="font-medium text-[#00E5C0] text-sm">Custom LLM Settings</h4>
                      
                      {/* Show existing key status */}
                      {isEditMode && formData.has_llm_api_key && !formData.llm_api_key && (
                        <div className="p-3 rounded-lg bg-[#00E5C0]/10 border border-green-500/30">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-400" />
                            <span className="text-[#00E5C0] text-sm">Custom API key is configured</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Provider Selection */}
                      <div>
                        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                          Provider
                        </label>
                        <select
                          value={formData.llm_provider || 'openai'}
                          onChange={(e) => setFormData({ ...formData, llm_provider: e.target.value, llm_model: '' })}
                          className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-purple-500 focus:outline-none text-white"
                        >
                          <option value="openai">OpenAI</option>
                          <option value="anthropic">Anthropic</option>
                          <option value="google">Google (Gemini)</option>
                        </select>
                      </div>
                      
                      {/* Model Selection */}
                      <div>
                        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                          Model
                        </label>
                        <select
                          value={formData.llm_model || ''}
                          onChange={(e) => setFormData({ ...formData, llm_model: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-purple-500 focus:outline-none text-white"
                        >
                          <option value="">Select a model...</option>
                          {formData.llm_provider === 'openai' && (
                            <>
                              <option value="gpt-4o">GPT-4o</option>
                              <option value="gpt-4o-mini">GPT-4o Mini</option>
                              <option value="gpt-4-turbo">GPT-4 Turbo</option>
                              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                            </>
                          )}
                          {formData.llm_provider === 'anthropic' && (
                            <>
                              <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</option>
                              <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                              <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                            </>
                          )}
                          {formData.llm_provider === 'google' && (
                            <>
                              <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                              <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
                              <option value="gemini-pro">Gemini Pro</option>
                            </>
                          )}
                        </select>
                      </div>
                      
                      {/* API Key */}
                      <div>
                        <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                          API Key
                          {formData.has_llm_api_key && !formData.llm_api_key && (
                            <span className="text-[#00E5C0] text-xs ml-2">● Currently configured</span>
                          )}
                          {formData.llm_api_key && (
                            <span className="text-[#00E5C0] text-xs ml-2">● New key entered</span>
                          )}
                        </label>
                        <input
                          type="password"
                          value={formData.llm_api_key}
                          onChange={(e) => setFormData({ ...formData, llm_api_key: e.target.value })}
                          placeholder={formData.has_llm_api_key ? 'Leave empty to keep current key' : 'sk-...'}
                          className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-purple-500 focus:outline-none text-white placeholder:text-[#7D8896] font-mono text-sm"
                        />
                        {formData.has_llm_api_key && (
                          <p className="text-xs text-[#7D8896] mt-1">
                            Leave empty to keep the existing key, or enter a new one to replace it.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Info about organization key */}
                  {formData.use_org_llm_key && (
                    <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                      <div className="flex items-start gap-3">
                        <WarningCircle className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[#3B82F6] font-medium text-sm mb-1">Using Organization Settings</p>
                          <p className="text-[#B0B8C4] text-sm">
                            This operator will use the LLM API key configured in your organization&apos;s settings. 
                            You can change this later if needed.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-[#1F2937] border-t border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex justify-between">
            {/* Back/Cancel button */}
            {step === 0 ? (
              <button
                onClick={handleClose}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                Cancel
              </button>
            ) : (
              <button
                onClick={() => {
                  if (step > 1) {
                    setStep(step - 1);
                  } else if (!isEditMode && !skipIntro) {
                    setStep(0); // Go back to intro
                  } else {
                    handleClose();
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                {step > 1 ? 'Back' : (!isEditMode && !skipIntro ? 'Back' : (hasUnsavedChanges ? 'Discard' : 'Cancel'))}
              </button>
            )}
            
            <div className="flex items-center gap-3">
              {/* Save Draft button - visible when creating or editing, on steps 1+ with unsaved changes */}
              {step > 0 && hasUnsavedChanges && formData.name.trim() && getDisplayStep() < totalSteps && (
                <button
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1A2030] hover:bg-zinc-600 transition-colors disabled:opacity-50 text-white"
                >
                  {savingDraft ? (
                    <>
                      <CircleNotch className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Square className="w-4 h-4" />
                      Save Draft
                    </>
                  )}
                </button>
              )}
              
              {/* Save button - visible in edit mode, but only on steps 1-6 (not the final step) */}
              {isEditMode && step < 7 && step > 0 && (
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 transition-colors disabled:opacity-50 text-white"
                >
                  {loading ? (
                    <>
                      <CircleNotch className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save
                    </>
                  )}
                </button>
              )}
              
              {/* Step 0: Get Started button */}
              {step === 0 && (
                <button
                  onClick={handleProceedFromIntro}
                  disabled={!acknowledgedRequirements}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] hover:opacity-90 transition-opacity disabled:opacity-50 text-white"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              
              {/* Steps 1-6: Next button */}
              {step > 0 && step < 7 && (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={step === 1 && !formData.name.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] hover:opacity-90 transition-opacity disabled:opacity-50 text-white"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
              
              {/* Step 7: Create/Save button */}
              {step === 7 && (
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r ${isEditMode ? 'from-blue-500 to-cyan-600' : 'from-[#00E5C0] to-[#00CCB0]'} hover:opacity-90 transition-opacity disabled:opacity-50 text-white`}
                >
                  {loading ? (
                    <>
                      <CircleNotch className="w-4 h-4 animate-spin" />
                      {isEditMode ? 'Saving...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {isEditMode ? <Check className="w-4 h-4" /> : <Sparkle className="w-4 h-4" />}
                      {isEditMode ? 'Save Changes' : 'Create Operator'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CREATE CONNECTOR MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const CreateConnectorModal = ({ isOpen, onClose, onSuccess, orgId }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [connector, setConnector] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    device_name: ''
  });

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Please enter a name for your connector', variant: 'destructive' });
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        },
        orgId
      );
      
      if (response.ok) {
        const data = await response.json();
        setConnector(data);
        setStep(2);
        toast({ title: 'Connector Created', description: 'Now follow the pairing instructions' });
      } else {
        const error = await response.json();
        // Handle both string and object error details
        const errorMessage = typeof error.detail === 'object' 
          ? error.detail.message || 'Failed to create connector'
          : error.detail || 'Failed to create connector';
        toast({ 
          title: 'Creation Failed', 
          description: errorMessage,
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Create error:', err);
      toast({ title: 'Error', description: 'Failed to create connector', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: 'Copied!', description: 'Pairing code copied to clipboard' });
  };

  const handleClose = () => {
    setStep(1);
    setConnector(null);
    setFormData({ name: '', device_name: '' });
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-lg bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="border-b border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Terminal className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">
                  {step === 1 ? 'Register Connector' : 'Pairing Instructions'}
                </h2>
                <p className="text-sm text-[#B0B8C4]">
                  {step === 1 ? 'Connect your local machine' : 'Complete the setup'}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors">
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                    Connector Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., My MacBook Pro"
                    className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-blue-500 focus:outline-none text-white placeholder:text-[#7D8896]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#F0F4F8] mb-2">
                    Device Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.device_name}
                    onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
                    placeholder="e.g., Work Laptop"
                    className="w-full px-4 py-3 rounded-lg bg-[#1E2836] border border-[rgba(255,255,255,0.08)] focus:border-blue-500 focus:outline-none text-white placeholder:text-[#7D8896]"
                  />
                </div>
              </motion.div>
            )}
            
            {step === 2 && connector && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Pairing Code */}
                <div className="p-6 rounded-xl bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)] text-center">
                  <p className="text-sm text-[#B0B8C4] mb-2">Your Pairing Code</p>
                  <div className="flex items-center justify-center gap-3">
                    <code className="text-3xl font-mono font-bold tracking-widest text-white">
                      {connector.pairing_code}
                    </code>
                    <button
                      onClick={() => copyToClipboard(connector.pairing_code)}
                      className="p-2 rounded-lg bg-[#1A2030] hover:bg-zinc-600 transition-colors"
                    >
                      {copied ? (
                        <Check className="w-5 h-5 text-[#00E5C0]" />
                      ) : (
                        <Copy className="w-5 h-5 text-[#F0F4F8]" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-[#7D8896] mt-3">
                    Expires in 10 minutes
                  </p>
                </div>
                
                {/* Instructions */}
                <div className="space-y-4">
                  <h4 className="font-medium text-[#F0F4F8]">Setup Instructions</h4>
                  
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 text-[#3B82F6] flex items-center justify-center text-sm font-medium flex-shrink-0">
                        1
                      </div>
                      <div>
                        <p className="text-sm text-[#F0F4F8]">Download the connector</p>
                        <code className="text-xs text-[#7D8896] bg-[#1E2836] px-2 py-1 rounded mt-1 inline-block">
                          pip install mm-connector
                        </code>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 text-[#3B82F6] flex items-center justify-center text-sm font-medium flex-shrink-0">
                        2
                      </div>
                      <div>
                        <p className="text-sm text-[#F0F4F8]">Run the connector with your pairing code</p>
                        <code className="text-xs text-[#7D8896] bg-[#1E2836] px-2 py-1 rounded mt-1 inline-block">
                          mm-connector pair {connector.pairing_code}
                        </code>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 text-[#3B82F6] flex items-center justify-center text-sm font-medium flex-shrink-0">
                        3
                      </div>
                      <div>
                        <p className="text-sm text-[#F0F4F8]">Keep the connector running</p>
                        <p className="text-xs text-[#7D8896] mt-1">
                          The connector will maintain a secure connection to your operators.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Footer */}
        <div className="border-t border-[rgba(255,255,255,0.08)] p-6">
          <div className="flex justify-between">
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
            >
              Cancel
            </button>
            
            {step === 1 ? (
              <button
                onClick={handleCreate}
                disabled={loading || !formData.name.trim()}
                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Register Connector
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleDone}
                className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 transition-opacity"
              >
                <CheckCircle className="w-4 h-4" />
                Done
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// OPERATOR DETAIL MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const OperatorDetailModal = ({ isOpen, onClose, operator, hostingActive, onSuccess, orgId }) => {
  const { toast } = useToast();
  const [actionLoading, setActionLoading] = useState(null);

  const handleStartOperator = async () => {
    if (!operator) return;
    setActionLoading('start');
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/${operator.id}/start`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': orgId
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail?.message || data.detail || 'Failed to start operator');
      }
      
      toast.success('Operator started successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseOperator = async () => {
    setActionLoading('pause');
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/${operator.id}/pause`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': orgId
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail?.message || data.detail || 'Failed to pause operator');
      }
      
      toast.success('Operator paused');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopOperator = async () => {
    setActionLoading('stop');
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/${operator.id}/stop`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': orgId
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail?.message || data.detail || 'Failed to stop operator');
      }
      
      toast.success('Operator stopped');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteOperator = async () => {
    if (!window.confirm('Are you sure you want to delete this operator? This action cannot be undone.')) {
      return;
    }
    
    setActionLoading('delete');
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/agents/${operator.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': orgId
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail?.message || data.detail || 'Failed to delete operator');
      }
      
      toast.success('Operator deleted');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Early return if modal is closed or no operator
  if (!isOpen || !operator) return null;

  const getStatusConfig = (status) => {
    const configs = {
      draft: { icon: Clock, color: 'text-[#B0B8C4] bg-[#1E2836]', label: 'Draft' },
      configured: { icon: GearSix, color: 'text-[#3B82F6] bg-blue-500/10', label: 'Configured' },
      active: { icon: Play, color: 'text-[#00E5C0] bg-[#00E5C0]/10', label: 'Active' },
      paused: { icon: Pause, color: 'text-[#F59E0B] bg-[#F59E0B]/10', label: 'Paused' },
      stopped: { icon: WarningCircle, color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Stopped' },
      error: { icon: WarningCircle, color: 'text-[#FF6B6B] bg-[#FF6B6B]/10', label: 'Error' },
    };
    return configs[status] || configs.draft;
  };

  const statusConfig = getStatusConfig(operator.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-[rgba(255,255,255,0.08)]">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5C0]/20 to-pink-500/20 flex items-center justify-center text-2xl">
                {operator.avatar_icon || '🤖'}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">{operator.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig.label}
                  </span>
                  {operator.memory_enabled && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-[#00E5C0] bg-[#00E5C0]/10">
                      <Brain className="w-3 h-3" />
                      Memory
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#1E2836] transition-colors"
            >
              <X className="w-5 h-5 text-[#B0B8C4]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {operator.description && (
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">Description</label>
              <p className="text-[#F0F4F8]">{operator.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">Created</label>
              <p className="text-[#F0F4F8] text-sm">
                {operator.created_at ? new Date(operator.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-1">Last Active</label>
              <p className="text-[#F0F4F8] text-sm">
                {operator.last_active_at ? new Date(operator.last_active_at).toLocaleString() : 'Never'}
              </p>
            </div>
          </div>

          {operator.capabilities && operator.capabilities.length > 0 && (
            <div>
              <label className="text-sm text-[#B0B8C4] block mb-2">Capabilities</label>
              <div className="flex flex-wrap gap-2">
                {operator.capabilities.map((cap, i) => (
                  <span key={i} className="px-2 py-1 rounded-lg bg-[#1E2836] text-[#F0F4F8] text-xs">
                    {cap}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Activation Status */}
          {!hostingActive && (
            <div className="p-3 rounded-lg bg-[#F59E0B]/10 border border-yellow-500/20">
              <div className="flex items-center gap-2 text-[#F59E0B] text-sm">
                <WarningCircle className="w-4 h-4" />
                <span>Activation required to run this operator</span>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-[rgba(255,255,255,0.08)] bg-[#1F2937]/50">
          <div className="flex items-center justify-between">
            <button
              onClick={handleDeleteOperator}
              disabled={actionLoading !== null}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] transition-colors disabled:opacity-50"
            >
              {actionLoading === 'delete' ? (
                <CircleNotch className="w-4 h-4 animate-spin" />
              ) : (
                <Trash className="w-4 h-4" />
              )}
              Trash
            </button>

            <div className="flex items-center gap-2">
              {operator.status === 'active' && (
                <>
                  <button
                    onClick={handlePauseOperator}
                    disabled={actionLoading !== null}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 text-[#F59E0B] transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'pause' ? (
                      <CircleNotch className="w-4 h-4 animate-spin" />
                    ) : (
                      <Pause className="w-4 h-4" />
                    )}
                    Pause
                  </button>
                  <button
                    onClick={handleStopOperator}
                    disabled={actionLoading !== null}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'stop' ? (
                      <CircleNotch className="w-4 h-4 animate-spin" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    Stop
                  </button>
                </>
              )}

              {operator.status === 'paused' && (
                <button
                  onClick={handleStartOperator}
                  disabled={actionLoading !== null || !hostingActive}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E5C0]/10 hover:bg-[#00E5C0]/20 text-[#00E5C0] transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'start' ? (
                    <CircleNotch className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Resume
                </button>
              )}

              {(operator.status === 'draft' || operator.status === 'configured' || operator.status === 'stopped') && hostingActive && (
                <button
                  onClick={handleStartOperator}
                  disabled={actionLoading !== null}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E5C0]/10 hover:bg-[#00E5C0]/20 text-[#00E5C0] transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'start' ? (
                    <CircleNotch className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Start
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Operators;
export { CreateOperatorModal };
